import { Component, EventEmitter, Injector, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Subject } from '@node_modules/rxjs';
import { debounceTime, take, takeUntil } from '@node_modules/rxjs/operators';
import { AppComponentBase } from '@shared/common/app-component-base';
import { AppConsts } from '@shared/commonHelper/AppConsts';
import { DataService } from '@app/shared/common/auth/data.service';
import { VfModalShellComponent } from '@app/shared/common/components/modal-shell/vf-modal-shell.component';
import { BreadcrumbItemDto, CreateFileInputDto, CreateFolderDto, FileParameter, GroupLinkDto, LinkServiceProxy, OneDriveServiceProxy } from '@shared/service-proxies/service-proxies';
import { ModalDirective } from 'ngx-bootstrap/modal';

/**
 * Deep-link target for {@link FileSelectorComponent.openAtFolder} — opens the STANDARD cloud-browse
 * popup already navigated into this folder, with the normal breadcrumb.
 */
export interface FileSelectorOpenAtFolder {
  /** Drive (document library) that holds the folder. Required. */
  driveId: string;
  /** The folder's drive-item id. Omit it — or pass the driveId — for the document-library ROOT. */
  itemId?: string;
  /** SharePoint site id. Without it the site crumb (and the libraries level) can't be resolved. */
  siteId?: string;
  /** Folder name shown in the breadcrumb until the real ancestry arrives. */
  name?: string;
}

/** A breadcrumb entry shaped like the drive items the existing crumb handlers/emitters expect. */
interface BrowseCrumb {
  id: string;
  name: string;
  extension: string;
  isSelected: boolean;
  isShow: boolean;
  folder: { childCount: number };
  parentReference: { driveId: string; siteId: string };
}

/** The mutable list-row shape the component decorates every Graph drive item with. */
interface BrowseRow {
  id?: string;
  name?: string;
  extension?: string;
  isSelected?: boolean;
  isShow?: boolean;
  isDisabled?: boolean;
}

/** A document library row inside `selectedSiteData.drives`. */
interface BrowseDriveRow extends BrowseRow {
  children?: BrowseRow[];
}

@Component({
  selector: 'vf-file-selector',
  templateUrl: './file-selector.component.html'
})
export class FileSelectorComponent extends AppComponentBase implements OnChanges, OnDestroy {
  @Input() entity: string;
  @Input() entityObj: any;
  @Input() isOpenFromSlider: boolean;
  @Input() showPopup: boolean;
  @Input() showFolderOnly: boolean;
  @Input() isSingleSelect: boolean;
  @Input() isOpenFolderDirectly: boolean;
  @Input() isFromProject: boolean;
  @Input() canSelectLibrary: boolean;
  @Input() folderDetails: any;
  @Input() showBg: boolean;
  @Input() initialTab: 'sharepoint' | 'onedrive' = 'sharepoint';
  /**
   * Optional deep link: open the STANDARD browse view already navigated INTO this folder, with the
   * normal breadcrumb (Sites › Site › Library › …folders) so the user can navigate up. Unset (the
   * default) => today's behaviour: the sites list, or the scoped level-0 view via isOpenFolderDirectly.
   */
  @Input() openAtFolder: FileSelectorOpenAtFolder;
  /** Optional label overrides for a scoped (isOpenFolderDirectly) picker. Unset => existing behaviour. */
  @Input() titleOverride: string;
  /** Replaces the footer-left "Current folder: X" hint when nothing is selected. */
  @Input() footerHintOverride: string;
  /** Hides the level-0 toolbar row (search + "Create new") — used by the attach pickers. */
  @Input() hideToolbar: boolean;
  @ViewChild('customModal') customModal: ModalDirective;
  @ViewChild('fsErrorShell') fsErrorShell: VfModalShellComponent;
  @Output() onClose = new EventEmitter();
  @Output() selectedItems = new EventEmitter();

  sharePointSitesData: Array<any> = new Array<any>();
  isSaving = false;
  private _lastSelectClick = 0;
  isUploadFiles = false;
  isShowAddItem = false;
  noResultFound = false;
  noSharePointSiteResultFound = false;
  isFolderEmpty = false;
  fileUploadError = false;
  isShowAddLibrary = false;
  isAllItemsSelected = false;
  isAllDrivesSelected = false;
  isAllLibrarySelected = false;
  isSharePointSitesLoading = false;
  level = 1;
  fileCtrl: any;
  selectedSiteData: any;
  selectedDriveData: any;
  selectedItem: Array<any> = new Array<any>();;
  driveCategoryId: number;
  driveItems: Array<any> = new Array<any>();
  fileRawData: Array<any> = new Array<any>();
  currentItemIndex: number = -1;
  itemsHistory: any[][] = [];
  uploadingItems: any[] = [];
  fileErrorMsg = '';
  searchTextOnL0 = '';
  searchTextOnL1 = '';
  activeCloudTab: 'sharepoint' | 'onedrive' = 'sharepoint';
  oneDriveItems: any[] = [];
  oneDriveLoading = false;
  oneDriveLoaded = false;
  oneDriveNextBatchLink = '';
  oneDriveFolderEmpty = false;
  searchTextOnOneDrive = '';
  oneDriveItemsFiltered: any[] = [];
  searchTextOnL2 = '';
  searchTextOnL3 = '';
  searchTextOnL4 = '';
  searchTextOnL5 = '';
  selectedDocType = 1;
  isShowCreateOptions = false;
  isItemSelected = false;
  noOfSelectedItems = 0;
  nextBatchLink = '';
  isShowInfoPopup = false;
  information = '';
  fsShellError: { title: string; message: string; cancelLabel?: string; saveLabel?: string } | undefined;
  private _fsLastErrorCode: string | null = null;
  private _fsPendingFileInput: CreateFileInputDto | null = null;
  private _fsPendingFolderInput: CreateFolderDto | null = null;
  private _fsPendingLevel: number = 0;
  private _fsPendingIsOneDrive: boolean = false;
  private _fsPendingFolderPlaceholderType: string = 'documentLibrary';
  selectedDriveId = '';
  isMoreDataLoading = false;
  documentName = '';
  isOpenFromCloudBrowse = false;
  fileNameError = false;
  fileNameErrorMessage = '';
  pattern = String.raw`^[^\\/:*?"<>|]+$`;
  private onSearching: Subject<any> = new Subject();
  private destroy$: Subject<void> = new Subject<void>();
  /** openAtFolder: the deep-linked site's libraries, held until the site crumb exists to hang them on. */
  private _deepLinkDrives: BrowseDriveRow[] = null;
  private _deepLinkSiteId = '';
  constructor(injector: Injector,
    private _linkService: LinkServiceProxy,
    private _oneDriveService: OneDriveServiceProxy,
    private dataService: DataService) {
    super(injector);
    this.onSearching
      .pipe(debounceTime(1000))
      .subscribe((result) => {
        this.getSharepointSites();
      });
  }
  ngOnChanges(changes: SimpleChanges): void {
    // Guard: with more than one bound input, ngOnChanges also fires for changes that don't include
    // showPopup — reading .currentValue off an absent change would throw.
    if (changes.showPopup) {
      this.doSomething(changes.showPopup.currentValue);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getSharepointSites() {
    if (!this.isSharePointSitesLoading) {
      this.isSharePointSitesLoading = true;
      this._oneDriveService.getSharePointSites(this.searchTextOnL1).subscribe(result => {
        this.isSharePointSitesLoading = false;
        if (!result.isError) {
          if (result.responseData.length > 0) {
            this.sharePointSitesData = result.responseData;
            this.sharePointSitesData.forEach(element => {
              element.isSelected = false;
            });
            this.noSharePointSiteResultFound = false;
          } else {
            this.sharePointSitesData = [];
            this.noSharePointSiteResultFound = true;
          }
        }
      });
    }
  }

  doSomething(condition) {
    if (condition) {
      this.customModal.show();
      this.resetData();
      if (this.openAtFolder && this.openAtFolder.driveId) {
        // Standard browse view, deep-linked into a folder (breadcrumb + navigate-up intact).
        this.openBrowseAtFolder(this.openAtFolder);
      } else if (this.isOpenFolderDirectly && this.folderDetails) {
        this.isOpenFromCloudBrowse = false;
        this.getFolderFilesDirectly();
      } else {
        this.isOpenFromCloudBrowse = true;
        this.getSharepointSites();
        this.initializeSearchOnLevel1();
      }
      this.updateBodyForCenterModalShow();
      // // $('body').addClass('deletePopup');
      // if ($('body > *').hasClass('modal-backdrop')) {
      //   $('body').removeClass('task-modal-body');
      //   $('.modal-backdrop:not(:first)').removeClass('modal-backdrop');
      //   // } else {
      //   //   $('body').removeClass('task-modal-body');
      //   $('body').addClass('center-modal-shown');
      // }
    }
  }

  getFolderFilesDirectly() {
    this.level = 0;
    this.initializeSearchOnLevel0();
    this.isSharePointSitesLoading = true;
    this.nextBatchLink = '';
    this._oneDriveService.getMsFiles(this.folderDetails.driveId, this.folderDetails.itemId, undefined, undefined).subscribe(result => {
      this.isSharePointSitesLoading = false;
      if (!result.isError) {
        if (result.responseData) {
          if (result.responseData['item1'] && result.responseData['item1'].length > 0) {
            this.driveItems = result.responseData['item1'];
            this.driveItems.forEach(element => {
              element.isSelected = false;
              element.isShow = true;
              element.isDisabled = this.showFolderOnly && element.extension !== 'folder';
            });
          } else {
            this.isFolderEmpty = true;
          }
          this.setItems(this.driveItems);
          if (result.responseData['item2']) {
            this.nextBatchLink = result.responseData['item2'];
          } else {
            this.nextBatchLink = '';
          }
        }
      }
    });
  }

  /**
   * openAtFolder: show the SAME view the user reaches by browsing (Sites › Site › Library › folders),
   * but already inside `target`. A nested folder lands on level 5 (the `selectedItem` stack view); a
   * document-library ROOT — driveId with no itemId, or itemId === driveId — lands on level 3, which is
   * exactly where clicking that library from the sites list lands you.
   * Three requests fire in parallel: the folder's children (the list), the folder's ancestry (the real
   * breadcrumb) and the levels above it (so the breadcrumb's up-navigation has data to show).
   */
  private openBrowseAtFolder(target: FileSelectorOpenAtFolder): void {
    const driveId: string = target.driveId;
    // Convention: a library root reports itemId === driveId; getMsFiles wants undefined for it.
    const itemId: string = (target.itemId && target.itemId !== driveId) ? target.itemId : undefined;
    const siteId: string = target.siteId || undefined;
    const isNested: boolean = !!itemId;

    this.activeCloudTab = 'sharepoint';
    this.isOpenFromCloudBrowse = true;
    this.isFolderEmpty = false;
    this.selectedItem = [];
    this.itemsHistory = [];
    this.currentItemIndex = -1;
    this.nextBatchLink = '';
    this.selectedDriveId = driveId;
    this._deepLinkDrives = null;
    this._deepLinkSiteId = siteId || '';
    this.level = isNested ? 5 : 3;

    // Provisional crumbs so the bar renders immediately; the real ancestry replaces them below.
    this.selectedDriveData = { id: driveId, name: target.name || 'Documents', children: [], isSelected: true, isShow: true };
    if (isNested) {
      this.selectedItem = [this.buildBrowseCrumb(itemId, target.name || '', driveId, siteId)];
    }
    setTimeout(() => {
      if (isNested) {
        this.initializeSearchOnLevel5();
      } else {
        this.initializeSearchOnLevel3();
      }
    }, 100);

    this.loadBrowseFolderChildren(driveId, itemId, isNested);
    this.loadBrowseBreadcrumb(driveId, target.itemId, siteId);
    this.prefetchBrowseAncestors(siteId, driveId);
  }

  /** A crumb carrying everything the existing handlers read: `id` + `parentReference.driveId` for
   *  click-to-navigate-back, and `folder` so receivers of a "current folder" emit detect a folder. */
  private buildBrowseCrumb(id: string, name: string, driveId: string, siteId: string): BrowseCrumb {
    return {
      id: id,
      name: name,
      extension: 'folder',
      isSelected: false,
      isShow: true,
      folder: { childCount: 0 },
      parentReference: { driveId: driveId, siteId: siteId }
    };
  }

  /** Loads the deep-linked folder's children into the list the target level renders. */
  private loadBrowseFolderChildren(driveId: string, itemId: string, isNested: boolean): void {
    this.isSharePointSitesLoading = true;
    this._oneDriveService.getMsFiles(driveId, itemId, undefined, undefined)
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        this.isSharePointSitesLoading = false;
        if (result.isError || !result.responseData) {
          return;
        }
        const rows: BrowseRow[] = result.responseData['item1'] || [];
        rows.forEach(row => {
          row.isSelected = false;
          row.isShow = true;
          row.isDisabled = this.showFolderOnly && row.extension !== 'folder';
        });
        if (rows.length === 0) {
          this.isFolderEmpty = true;
        }
        if (isNested) {
          this.driveItems = rows;
          this.setItems(this.driveItems);
        } else if (this.selectedDriveData) {
          this.selectedDriveData.children = rows;
        }
        this.nextBatchLink = result.responseData['item2'] || '';
      }, () => {
        this.isSharePointSitesLoading = false;
      });
  }

  /** Replaces the provisional crumbs with the folder's real ancestry (site › library › folders…). */
  private loadBrowseBreadcrumb(driveId: string, itemId: string, siteId: string): void {
    this._oneDriveService.getFolderBreadcrumb(driveId, itemId || undefined, siteId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result.isError || !result.responseData || result.responseData.length === 0) {
          return;
        }
        this.applyBrowseBreadcrumb(result.responseData, driveId, siteId);
      }, () => {
        // The breadcrumb is cosmetic — on failure the provisional library/folder crumbs stay.
      });
  }

  private applyBrowseBreadcrumb(trail: BreadcrumbItemDto[], driveId: string, siteId: string): void {
    // Drop a late response once the user has navigated out of the deep-linked library.
    if (!this.selectedDriveData || this.selectedDriveData.id !== driveId) {
      return;
    }
    const folders: BrowseCrumb[] = [];
    trail.forEach(crumb => {
      if (crumb.type === 'site') {
        this.selectedSiteData = { id: crumb.id, name: crumb.name, displayName: crumb.name, drives: [] };
      } else if (crumb.type === 'documentLibrary') {
        this.selectedDriveData.name = crumb.name || this.selectedDriveData.name;
      } else if (crumb.type === 'folder') {
        folders.push(this.buildBrowseCrumb(crumb.id, crumb.name, crumb.driveId || driveId, siteId));
      }
    });
    if (folders.length > 0 && this.level > 4) {
      this.selectedItem = folders;
    }
    this.applyDeepLinkDrives();
  }

  /** Warms the levels ABOVE the deep-linked folder, so clicking "Sites" / the site crumb shows a list
   *  instead of an empty one (a deep link never walks through those levels). */
  private prefetchBrowseAncestors(siteId: string, driveId: string): void {
    if (this.sharePointSitesData.length === 0) {
      this._oneDriveService.getSharePointSites('')
        .pipe(takeUntil(this.destroy$))
        .subscribe(result => {
          if (result.isError || !result.responseData || this.sharePointSitesData.length > 0) {
            return;
          }
          this.sharePointSitesData = result.responseData;
          this.sharePointSitesData.forEach(site => site.isSelected = false);
        }, () => {
          // Non-fatal: the sites list reloads when the user switches tabs.
        });
    }
    if (!siteId) {
      return;
    }
    this._oneDriveService.getDrivesBySiteId(siteId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result.isError || !result.responseData || result.responseData.length === 0) {
          return;
        }
        const drives: BrowseDriveRow[] = result.responseData as BrowseDriveRow[];
        drives.forEach(drive => {
          drive.isSelected = false;
          drive.isShow = true;
        });
        // Keep ONE object identity for the current library: level 3's paging/back handlers look it up
        // inside selectedSiteData.drives and expect it to be the same row as selectedDriveData.
        const current: BrowseDriveRow = drives.filter(drive => drive.id === driveId)[0];
        if (current && this.selectedDriveData && this.selectedDriveData.id === driveId) {
          current.isSelected = true;
          current.children = this.selectedDriveData.children || [];
          current.name = this.selectedDriveData.name || current.name;
          this.selectedDriveData = current;
        } else if (!current && this.selectedDriveData) {
          drives.push(this.selectedDriveData);
        }
        this._deepLinkDrives = drives;
        this.applyDeepLinkDrives();
      }, () => {
        // Non-fatal: the site's libraries reload when the user navigates up to it.
      });
  }

  /** The libraries and the site crumb arrive independently — hang the former on the latter, whichever
   *  lands second. */
  private applyDeepLinkDrives(): void {
    if (!this._deepLinkDrives || !this.selectedSiteData) {
      return;
    }
    if (this._deepLinkSiteId && this.selectedSiteData.id !== this._deepLinkSiteId) {
      return;
    }
    this.selectedSiteData.drives = this._deepLinkDrives;
  }

  resetData() {
    this.level = 1;
    this.isItemSelected = false;
    this.sharePointSitesData = [];
    this.driveItems = [];
    this.selectedItem = [];
    this.driveCategoryId = undefined;
    this.fileRawData = [];
    this.itemsHistory = [];
    this.isSaving = false;
    this._lastSelectClick = 0;
    this.isUploadFiles = false;
    this.isShowAddItem = false;
    this.noResultFound = false;
    this.noSharePointSiteResultFound = false;
    this.isFolderEmpty = false;
    this.fileUploadError = false;
    this.isShowAddLibrary = false;
    this.isAllItemsSelected = false;
    this.isAllDrivesSelected = false;
    this.isAllLibrarySelected = false;
    this.isSharePointSitesLoading = false;
    this.selectedSiteData = null;
    this.selectedDriveData = null;
    this.fileErrorMsg = '';
    this.searchTextOnL2 = '';
    this.searchTextOnL3 = '';
    this.searchTextOnL4 = '';
    this.searchTextOnL5 = '';
    this.selectedDocType = 1;
    this.noOfSelectedItems = 0;
    this.activeCloudTab = this.initialTab;
    this.oneDriveItems = [];
    this.oneDriveItemsFiltered = [];
    this.oneDriveLoaded = false;
    this.oneDriveLoading = false;
    this.oneDriveFolderEmpty = false;
    this.oneDriveNextBatchLink = '';
    this.searchTextOnOneDrive = '';
    this._deepLinkDrives = null;
    this._deepLinkSiteId = '';
  }

  clickClose() {
    if ($('#CreateFileModal').hasClass('show')) {
      $('body').addClass('center-modal-shown');
    } else {
      this.updateBodyForCenterModalHide();
    }
    this.customModal.hide();
    this.onClose.emit();
  }

  private initializeSearchOnLevel0() {
    this.noResultFound = false;
    this.searchTextOnL0 = '';
    $('.level-0-search .input-group-append').hide();
    $('.level-0-search .form-control').on('keyup blur', function () {
      let div = $('.level-0-search .input-group-append');
      if ($('.level-0-search .form-control').val() !== '') {
        $(this).addClass('focus');
        div.show();
      } else {
        div.hide();
        $(this).removeClass('focus');
      }
    });
    $('.level-0-search .input-group-append').on('click', function () {
      $('.level-0-search .form-control').trigger('focus');
    });
  }

   private initializeSearchOnLevel1() {
    this.noSharePointSiteResultFound = false;
    this.searchTextOnL1 = '';
    $('.level-1-search .input-group-append').hide();
    $('.level-1-search .form-control').on('keyup blur', function () {
      let div = $('.level-1-search .input-group-append');
      if ($('.level-1-search .form-control').val() !== '') {
        $(this).addClass('focus');
        div.show();
      } else {
        div.hide();
        $(this).removeClass('focus');
      }
    });
    $('.level-1-search .input-group-append').on('click', function () {
      $('.level-1-search .form-control').trigger('focus');
    });
  }

  private initializeSearchOnLevel2() {
    this.noResultFound = false;
    this.searchTextOnL2 = '';
    $('.level-2-search .input-group-append').hide();
    $('.level-2-search .form-control').on('keyup blur', function () {
      let div = $('.level-2-search .input-group-append');
      if ($('.level-2-search .form-control').val() !== '') {
        $(this).addClass('focus');
        div.show();
      } else {
        div.hide();
        $(this).removeClass('focus');
      }
    });
    $('.level-2-search .input-group-append').on('click', function () {
      $('.level-2-search .form-control').trigger('focus');
    });
  }

  private initializeSearchOnLevel3() {
    this.noResultFound = false;
    this.searchTextOnL3 = '';
    $('.level-3-search .input-group-append').hide();
    $('.level-3-search .form-control').on('keyup blur', function () {
      let div = $('.level-3-search .input-group-append');
      if ($('.level-3-search .form-control').val() !== '') {
        $(this).addClass('focus');
        div.show();
      } else {
        div.hide();
        $(this).removeClass('focus');
      }
    });
    $('.level-3-search .input-group-append').on('click', function () {
      $('.level-3-search .form-control').trigger('focus');
    });
  }

  private initializeSearchOnLevel4() {
    this.noResultFound = false;
    this.searchTextOnL4 = '';
    $('.level-4-search .input-group-append').hide();
    $('.level-4-search .form-control').on('keyup blur', function () {
      let div = $('.level-4-search .input-group-append');
      if ($('.level-4-search .form-control').val() !== '') {
        $(this).addClass('focus');
        div.show();
      } else {
        div.hide();
        $(this).removeClass('focus');
      }
    });
    $('.level-4-search .input-group-append').on('click', function () {
      $('.level-4-search .form-control').trigger('focus');
    });
  }

  private initializeSearchOnLevel5() {
    this.noResultFound = false;
    this.searchTextOnL5 = '';
    $('.level-5-search .input-group-append').hide();
    $('.level-5-search .form-control').on('keyup blur', function () {
      let div = $('.level-5-search .input-group-append');
      if ($('.level-5-search .form-control').val() !== '') {
        $(this).addClass('focus');
        div.show();
      } else {
        div.hide();
        $(this).removeClass('focus');
      }
    });
    $('.level-5-search .input-group-append').on('click', function () {
      $('.level-5-search .form-control').trigger('focus');
    });
  }

  goToDrivesBySiteId(site) {
    this.isSharePointSitesLoading = true;
    this.isFolderEmpty = false;
    this.level = 2;
    setTimeout(() => {
      this.initializeSearchOnLevel2();
    }, 100);
    this.isShowAddLibrary = false;
    this.selectedSiteData = site;
    this.selectedSiteData.drives = [];
    this._oneDriveService.getDrivesBySiteId(site.id).subscribe(result => {
      this.isSharePointSitesLoading = false;
      if (!result.isError) {
        this.level = 2;
        if (result.responseData.length > 0) {
          this.sharePointSitesData.forEach(element => {
            if (element.id === site.id) {
              element.isSelected = true;
              element.drives = result.responseData;
              element.drives.forEach(drive => {
                drive.isSelected = false;
                drive.isShow = true;
              });
              this.selectedSiteData = element;
              this.checkIfItemsToShowOnLevel2();
            }
          });
        } else {
          this.isFolderEmpty = true;
        }
      }
    });
  }

  selectAllLibrary() {
    this.isAllLibrarySelected = !this.isAllLibrarySelected;
    this.selectedSiteData.drives.forEach(element => {
      element.isSelected = this.isAllLibrarySelected;
    });
  }

  backToSelectLocation() {
    this.level = 1;
    this.isFolderEmpty = false;
    this.isShowAddLibrary = false;
    this.selectedSiteData = null;
    this.isItemSelected = false;
  }

  selectDrive(drive) {
    if (this.isSingleSelect) {
      this.selectedSiteData.drives.forEach(element => {
        element.isSelected = false;
      });
    }
    this.isAllLibrarySelected = false;
    drive.isSelected = !drive.isSelected;
    this.isItemSelected = drive.isSelected ? true : false;
    if (this.isSingleSelect) {
      this.noOfSelectedItems = this.isItemSelected ? 1 : 0;
    }
  }

  selectDriveAndSave() {
    if (this.isSaving || Date.now() - this._lastSelectClick < 1000) return;
    this._lastSelectClick = Date.now();
    this.isSaving = true;
    let noOfSelectedDrives = this.selectedSiteData.drives.filter(x => x.isSelected).length;
    if (noOfSelectedDrives === 0) {
      this.isSaving = false;
      let options = this.toastrOptions;
      this.toastr
        .error('Please select at least one library.',
          null, options);
      return;
    }
    const checkedLibs = this.selectedSiteData.drives.filter(x => x.isSelected);
    this.selectedItems.emit([checkedLibs, this.selectedSiteData.id, this.displayTrailFor(checkedLibs)]);
    this.clickClose();
  }

  createLinkToDocLibrary(item, noOfSelectedDrives): void {
    let input: GroupLinkDto = new GroupLinkDto();

    input.type = 3; // 3 for shortcut
    input.name = item.name;
    if (!item.webUrl.startsWith('http')) {
      input.url = 'http://' + item.webUrl;
    } else {
      input.url = item.webUrl;
    }
    input.isShareFile = false; // this.oneDrivePath ? true : false;
    input.isEveryone = true;
    // input.path = this.groupLinkObj.path;
    input.keyId = item.id;
    input.extension = 'documentLibrary';
    input.driveCategoryId = this.driveCategoryId;

    input.driveId = item.id;
    input.siteId = this.selectedSiteData.id;
    this._linkService.createOrUpdateGroupLink(input).subscribe(result => {
      if (!result.isError) {
        if (noOfSelectedDrives === 1) {
          // this.dataService.refreshFilesSlider.next();
          let options = this.toastrOptions;
          this.toastr
            .success('Added to team drive.',
              null, options);
          this.clickClose();
          this.isSaving = false;
        }
      } else {
        if (result.errorMessage.toLocaleLowerCase() === 'duplicate') {
          this.isSaving = false;
          let options = this.toastrOptions;
          this.toastr
            .error('Item already added to team drive.',
              null, options);
        } else {
          this.isSaving = false;
          let options = this.toastrOptions;
          this.toastr
            .error('Error while adding to team drive.',
              null, options);
        }
      }
    });
  }

  goToChildrenOfDrive(drive) {
    this.isSharePointSitesLoading = true;
    this.level = 3;
    setTimeout(() => {
      this.initializeSearchOnLevel3();
    }, 100);
    this.isFolderEmpty = false;
    if (this.selectedSiteData?.drives?.length) {
      this.selectedSiteData.drives.forEach(element => {
        if (element.id === drive.id) {
          this.selectedDriveId = drive.id;
          element.isSelected = true;
          element.children = [];
          this.selectedDriveData = element;
        }
      });
    } else {
      // Deep-linked (openAtFolder) with no site context: the drive row IS the current library.
      this.selectedDriveId = drive.id;
      drive.isSelected = true;
      drive.children = [];
      this.selectedDriveData = drive;
    }
    // this._oneDriveService.getSharePointDriveChildrenByDriveId(drive.id).subscribe(result => {
    //   this.isSharePointSitesLoading = false;
    //   if (!result.isError) {
    //     if (result.responseData.length > 0) {
    //       this.selectedSiteData.drives.forEach(element => {
    //         if (element.id === drive.id) {
    //           element.isSelected = true;
    //           element.children = result.responseData;
    //           element.children.forEach(child => {
    //             child.isSelected = false;
    //             if (this.showFolderOnly) {
    //               if (child.extension === 'folder') {
    //                 child.isShow = true;
    //               } else {
    //                 child.isShow = false;
    //               }
    //             } else {
    //               child.isShow = true;
    //             }
    //           });
    //           this.selectedDriveData = element;
    //         }
    //       });
    //     } else {
    //       this.isFolderEmpty = true;
    //     }
    //   }
    // });

    this.nextBatchLink = '';
    this._oneDriveService.getMsFiles(drive.id, undefined, undefined, undefined).subscribe(result => {
      this.isSharePointSitesLoading = false;
      if (!result.isError) {
        this.driveItems = [];
        if (result.responseData) {
          if (result.responseData['item1'] && result.responseData['item1'].length > 0) {
            const target = this.selectedSiteData?.drives?.length
              ? this.selectedSiteData.drives.filter(element => element.id === drive.id)[0]
              : drive;
            if (target) {
              target.isSelected = true;
              target.children = result.responseData['item1'];
              target.children.forEach(child => {
                child.isSelected = false;
                child.isShow = true;
                child.isDisabled = this.showFolderOnly && child.extension !== 'folder';
              });
              this.selectedDriveData = target;
            }
          } else {
            this.isFolderEmpty = true;
          }
          if (result.responseData['item2']) {
            this.nextBatchLink = result.responseData['item2'];
          } else {
            this.nextBatchLink = '';
          }
        }
      } else {
        // show error
      }
    });

  }

  getMoreChildrenOfDrive(event?: any) {
    if (this.nextBatchLink !== '' && !this.isMoreDataLoading) {
      this.isMoreDataLoading = true;
      this._oneDriveService.getMsFiles(this.selectedDriveId, undefined, this.nextBatchLink, undefined).subscribe(result => {
        this.isMoreDataLoading = false;
        if (!result.isError) {
          if (result.responseData) {
            if (result.responseData['item1'] && result.responseData['item1'].length > 0) {
              const target = this.selectedSiteData?.drives?.length
                ? this.selectedSiteData.drives.filter(element => element.id === this.selectedDriveId)[0]
                : this.selectedDriveData;
              if (target) {
                target.isSelected = true;
                target.children.push(...result.responseData['item1']);
                target.children.forEach(child => {
                  child.isSelected = false;
                  child.isShow = true;
                  child.isDisabled = this.showFolderOnly && child.extension !== 'folder';
                });
                this.selectedDriveData = target;
              }
            }
            if (result.responseData['item2']) {
              this.nextBatchLink = result.responseData['item2'];
            } else {
              this.nextBatchLink = '';
            }
          }
        }
      });
    }
  }

  backToSelectedDrive() {
    this.level = 2;
    this.selectedDriveData = null;
    this.isFolderEmpty = false;
    this.isShowAddItem = false;
    this.isShowAddLibrary = false;
    this.isItemSelected = false;
    setTimeout(() => {
      this.initializeSearchOnLevel2();
    }, 100);
    this.selectedSiteData.drives.forEach(element => {
      element.isSelected = false;
      element.isShow = true;
      element.isDisabled = this.showFolderOnly && element.extension !== 'folder' && !!element.extension;
    });
  }

  getDriveItems(item) {
    this.isSharePointSitesLoading = true;
    this.level = 5;
    setTimeout(() => {
      this.initializeSearchOnLevel5();
    }, 100);
    this.selectedItem.push(item);
    if (this.selectedDriveData?.children) {
      this.selectedDriveData.children.forEach(element => {
        if (element.id === item.id) {
          element.isSelected = true;
        }
      });
    }
    this.isFolderEmpty = false;
    // this._oneDriveService.getDriveItemsByItemId(item.parentReference.driveId, item.id).subscribe(result => {
    //   this.isSharePointSitesLoading = false;
    //   if (!result.isError) {
    //     if (result.responseData.length > 0) {
    //       this.driveItems = result.responseData;
    //       this.driveItems.forEach(element => {
    //         element.isSelected = false;
    //         if (this.showFolderOnly) {
    //           if (element.extension === 'folder') {
    //             element.isShow = true;
    //           } else {
    //             element.isShow = false;
    //           }
    //         } else {
    //           element.isShow = true;
    //         }
    //       });
    //     } else {
    //       this.isFolderEmpty = true;
    //     }
    //     this.setItems(this.driveItems);
    //   }
    // });

    this.nextBatchLink = '';
    this._oneDriveService.getMsFiles(item.parentReference.driveId, item.parentReference.driveId === item.id ? undefined : item.id, undefined, undefined).subscribe(result => {
      this.isSharePointSitesLoading = false;
      if (!result.isError) {
        if (result.responseData) {
          if (result.responseData['item1'] && result.responseData['item1'].length > 0) {
            this.driveItems = result.responseData['item1'];
            this.driveItems.forEach(element => {
              element.isSelected = false;
              element.isShow = true;
              element.isDisabled = this.showFolderOnly && element.extension !== 'folder';
            });
          } else {
            this.isFolderEmpty = true;
          }
          this.setItems(this.driveItems);
          if (result.responseData['item2']) {
            this.nextBatchLink = result.responseData['item2'];
          } else {
            this.nextBatchLink = '';
          }
        }
      }
    });
  }

  backToSelectedDriveItem() {
    this.level = 3;
    this.driveItems = [];
    this.isItemSelected = false;
    this.isFolderEmpty = false;
    setTimeout(() => {
      this.initializeSearchOnLevel3();
    }, 100);
    this.selectedDriveData.children.forEach(element => {
      element.isSelected = false;
      element.isShow = true;
      element.isDisabled = this.showFolderOnly && element.extension !== 'folder';
    });
  }

  goBack(): void {
    if (this.currentItemIndex > 0) {
      this.currentItemIndex--;
      this.driveItems = this.itemsHistory[this.currentItemIndex];
      this.driveItems.forEach(element => {
        element.isSelected = false;
        element.isShow = true;
        element.isDisabled = this.showFolderOnly && element.extension !== 'folder';
      });
      this.itemsHistory.pop();
    } else {
      this.backToSelectLocation();
      // clear the item history
      this.itemsHistory = [];
    }
    this.isFolderEmpty = false;
    this.isShowAddItem = false;
    this.isShowAddLibrary = false;
    if (this.selectedItem.length > 0) {
      this.selectedItem.pop();
    }
    this.isAllItemsSelected = false;
    this.isItemSelected = false;
  }

  goBackSP(): void {
    if (this.currentItemIndex > 0) {
      this.currentItemIndex--;
      this.driveItems = this.itemsHistory[this.currentItemIndex];
      this.driveItems.forEach(element => {
        element.isSelected = false;
        element.isShow = true;
        element.isDisabled = this.showFolderOnly && element.extension !== 'folder';
      });
      this.itemsHistory.pop();
    } else {
      this.backToSelectedDriveItem();
      // clear the item history
      this.itemsHistory = [];
    }
    this.isFolderEmpty = false;
    this.isShowAddItem = false;
    this.isShowAddLibrary = false;
    if (this.selectedItem.length > 0) {
      this.selectedItem.pop();
    }
    this.isAllItemsSelected = false;
    this.isItemSelected = false;
    if (this.itemsHistory.length === 1 && this.isFromProject) {
      this.level = 0;
    }
  }

  setItems(items: any[]): void {
    this.itemsHistory.push([...items]);
    this.currentItemIndex = this.itemsHistory.length - 1;
  }

  refreshItemsInItemsHistory(items: any[]): void {
    this.itemsHistory[this.currentItemIndex] = items;
  }

  /** Build a placeholder file row to insert into the list BEFORE calling
   *  createOneDriveFile, so the user sees it immediately. The real id and url
   *  come from the response and patch this same row via _tempId. */
  private buildPlaceholderFileItem(input: any): any {
    const ext = (input.extension || '').replace('.', '').toLowerCase();
    const ts = Date.now();
    return {
      id: 'pending_' + ts,
      _tempId: 'temp_' + ts + '_' + Math.random().toString(36).substr(2, 9),
      name: input.name + (input.extension || ''),
      extension: ext,
      webUrl: '',
      lastModifiedDateTime: new Date().toISOString(),
      parentReference: {
        driveId: input.driveId,
        siteId: input.siteId,
        id: input.itemId
      },
      file: { mimeType: '' },
      isSelected: false,
      isShow: !this.showFolderOnly,
      isDisabled: this.showFolderOnly,
      isPending: true
    };
  }

  /** Build a placeholder folder/library row, inserted before the create API
   *  call. Patched with real id/url on success, removed on failure. */
  private buildPlaceholderFolderItem(input: any, isLibrary = false, driveType?: string): any {
    const ts = Date.now();
    return {
      id: 'pending_' + ts,
      _tempId: 'temp_' + ts + '_' + Math.random().toString(36).substr(2, 9),
      name: input.name,
      extension: isLibrary ? 'documentLibrary' : 'folder',
      webUrl: '',
      lastModifiedDateTime: new Date().toISOString(),
      parentReference: {
        driveId: input.driveId,
        siteId: input.siteId,
        id: input.itemId,
        driveType: driveType
      },
      folder: { childCount: 0 },
      isSelected: false,
      isShow: true,
      isDisabled: false,
      isPending: true
    };
  }

  /** Patch an in-flight placeholder row with the real values from
   *  createOneDriveFile's response. */
  private applyFileResponseToItem(item: any, response: any): void {
    if (!item || !response) return;
    if (response.keyId) item.id = response.keyId;
    if (response.fileName) item.name = response.fileName;
    if (response.url) item.webUrl = response.url;
    item.parentReference = {
      ...(item.parentReference || {}),
      driveId: response.driveId || item.parentReference?.driveId,
      siteId: response.siteId || item.parentReference?.siteId
    };
    item.isPending = false;
  }

  /** Patch an in-flight placeholder folder row with the real values from
   *  createFolder/createDocumentLibrary's response. */
  private applyFolderResponseToItem(item: any, response: any): void {
    if (!item || !response) return;
    const realId = response.folderId || response.driveId;
    if (realId) item.id = realId;
    if (response.folderUrl) item.webUrl = response.folderUrl;
    item.parentReference = {
      ...(item.parentReference || {}),
      driveId: response.driveId || item.parentReference?.driveId,
      siteId: response.siteId || item.parentReference?.siteId,
      driveType: response.driveType || item.parentReference?.driveType
    };
    item.isPending = false;
  }

  /** Inserts an optimistic placeholder into the list backing the current
   *  level so the user sees it at the top instantly, before the create API
   *  call completes. Returns the same item for later patching/removal. */
  private insertOptimisticItemForLevel(item: any, level: number, isOneDrive?: boolean): any {
    if (!item) return item;
    if (level === 1 && this.activeCloudTab === 'onedrive') {
      this.oneDriveItems.unshift(item);
      this.oneDriveItemsFiltered.unshift(item);
      this.oneDriveFolderEmpty = false;
    } else if (level === 3) {
      if (!this.selectedDriveData) return item;
      if (!this.selectedDriveData.children) this.selectedDriveData.children = [];
      this.selectedDriveData.children.unshift(item);
      this.isFolderEmpty = false;
    } else {
      this.driveItems.unshift(item);
      this.isFolderEmpty = false;
      // Keep navigation history in sync so back/forward shows the new item too.
      if (this.itemsHistory && this.itemsHistory.length > 0
        && this.currentItemIndex != null
        && this.itemsHistory[this.currentItemIndex]) {
        this.itemsHistory[this.currentItemIndex] = this.driveItems;
      }
    }
    return item;
  }

  /** Find a placeholder row by tempId across whichever list backs the level. */
  private findOptimisticItemByTempId(tempId: string, level: number): any {
    if (!tempId) return null;
    if (level === 1 && this.activeCloudTab === 'onedrive') {
      return this.oneDriveItems.find(x => x._tempId === tempId)
        || this.oneDriveItemsFiltered.find(x => x._tempId === tempId);
    }
    if (level === 3 && this.selectedDriveData?.children) {
      return this.selectedDriveData.children.find(x => x._tempId === tempId);
    }
    return this.driveItems.find(x => x._tempId === tempId);
  }

  /** Remove a failed placeholder row from the list backing the level. */
  private removeOptimisticItem(tempId: string, level: number): void {
    if (!tempId) return;
    const removeFrom = (list: any[]) => {
      const idx = list.findIndex(x => x._tempId === tempId);
      if (idx > -1) list.splice(idx, 1);
    };
    if (level === 1 && this.activeCloudTab === 'onedrive') {
      removeFrom(this.oneDriveItems);
      removeFrom(this.oneDriveItemsFiltered);
    } else if (level === 3 && this.selectedDriveData?.children) {
      removeFrom(this.selectedDriveData.children);
    } else {
      removeFrom(this.driveItems);
      if (this.itemsHistory && this.itemsHistory.length > 0
        && this.currentItemIndex != null
        && this.itemsHistory[this.currentItemIndex]) {
        this.itemsHistory[this.currentItemIndex] = this.driveItems;
      }
    }
  }

  goToL5FromL0(item: any): void {
    this.clearOneDriveSelections();
    this.level = 5;
    this.removeSearchOnLevel5();
    this.isFolderEmpty = false;
    this.isSharePointSitesLoading = true;
    this.selectedItem.push(item);
    // this._oneDriveService.getDriveItemsByItemId(item.parentReference.driveId, item.id).subscribe(result => {
    //   this.isSharePointSitesLoading = false;
    //   if (!result.isError) {
    //     if (result.responseData.length > 0) {
    //       this.driveItems = result.responseData;
    //       this.driveItems.forEach(element => {
    //         element.isSelected = false;
    //         if (this.showFolderOnly) {
    //           if (element.extension === 'folder') {
    //             element.isShow = true;
    //           } else {
    //             element.isShow = false;
    //           }
    //         } else {
    //           element.isShow = true;
    //         }
    //       });
    //     } else {
    //       this.isFolderEmpty = true;
    //     }
    //     this.setItems(this.driveItems);
    //   }
    // });

    this.nextBatchLink = '';
    this._oneDriveService.getMsFiles(item.parentReference.driveId, item.parentReference.driveId === item.id ? undefined : item.id, undefined, undefined).subscribe(result => {
      this.isSharePointSitesLoading = false;
      if (!result.isError) {
        if (result.responseData) {
          if (result.responseData['item1'] && result.responseData['item1'].length > 0) {
            this.driveItems = result.responseData['item1'];
            this.driveItems.forEach(element => {
              element.isSelected = false;
              element.isShow = true;
              element.isDisabled = this.showFolderOnly && element.extension !== 'folder';
            });
          } else {
            this.isFolderEmpty = true;
          }
          this.setItems(this.driveItems);
          if (result.responseData['item2']) {
            this.nextBatchLink = result.responseData['item2'];
          } else {
            this.nextBatchLink = '';
          }
        }
      }
    });
  }

  onItemSelected(item: any): void {
    this.removeSearchOnLevel4();
    this.isFolderEmpty = false;
    this.isSharePointSitesLoading = true;
    this.selectedItem.push(item);
    // this._oneDriveService.getDriveItemsByItemId(item.parentReference.driveId, item.id).subscribe(result => {
    //   this.isSharePointSitesLoading = false;
    //   if (!result.isError) {
    //     if (result.responseData.length > 0) {
    //       this.driveItems = result.responseData;
    //       this.driveItems.forEach(element => {
    //         element.isSelected = false;
    //         if (this.showFolderOnly) {
    //           if (element.extension === 'folder') {
    //             element.isShow = true;
    //           } else {
    //             element.isShow = false;
    //           }
    //         } else {
    //           element.isShow = true;
    //         }
    //       });
    //     } else {
    //       this.isFolderEmpty = true;
    //     }
    //     this.setItems(this.driveItems);
    //   }
    // });

    this.nextBatchLink = '';
    this._oneDriveService.getMsFiles(item.parentReference.driveId, item.parentReference.driveId === item.id ? undefined : item.id, undefined, undefined).subscribe(result => {
      this.isSharePointSitesLoading = false;
      if (!result.isError) {
        if (result.responseData) {
          if (result.responseData['item1'] && result.responseData['item1'].length > 0) {
            this.driveItems = result.responseData['item1'];
            this.driveItems.forEach(element => {
              element.isSelected = false;
              element.isShow = true;
              element.isDisabled = this.showFolderOnly && element.extension !== 'folder';
            });
          } else {
            this.isFolderEmpty = true;
          }
          this.setItems(this.driveItems);
          if (result.responseData['item2']) {
            this.nextBatchLink = result.responseData['item2'];
          } else {
            this.nextBatchLink = '';
          }
        }
      }
    });
  }

  getMyFilesOnOneDrive(): void {
    this.isSharePointSitesLoading = true;
    this.level = 4;
    setTimeout(() => {
      this.initializeSearchOnLevel4();
    }, 100);
    this.nextBatchLink = '';
    this.selectedItem.push({ name: 'My Files' });
    // this._oneDriveService.getMyFilesOnOneDrive().subscribe(result => {
    //   this.isSharePointSitesLoading = false;
    //   if (!result.isError) {
    //     if (result.responseData.length > 0) {
    //       this.driveItems = result.responseData;
    //       this.driveItems.forEach(element => {
    //         element.isSelected = false;
    //         if (this.showFolderOnly) {
    //           if (element.extension === 'folder') {
    //             element.isShow = true;
    //           } else {
    //             element.isShow = false;
    //           }
    //         } else {
    //           element.isShow = true;
    //         }
    //       });
    //     } else {
    //       this.isFolderEmpty = true;
    //     }
    //     this.setItems(this.driveItems);
    //   }
    // });
    this._oneDriveService.getMsFiles(undefined, undefined, undefined, undefined).subscribe(result => {
      this.isSharePointSitesLoading = false;
      if (!result.isError) {
        if (result.responseData) {
          if (result.responseData['item1'] && result.responseData['item1'].length > 0) {
            this.driveItems = result.responseData['item1'];
            this.driveItems.forEach(element => {
              element.isSelected = false;
              element.isShow = true;
              element.isDisabled = this.showFolderOnly && element.extension !== 'folder';
            });
          } else {
            this.isFolderEmpty = true;
          }
          this.setItems(this.driveItems);
          if (result.responseData['item2']) {
            this.nextBatchLink = result.responseData['item2'];
          } else {
            this.nextBatchLink = '';
          }
        }
      }
    });
  }

  loadOneDriveFilesForTab(): void {
    if (this.oneDriveLoaded || this.oneDriveLoading) { return; }
    this.oneDriveLoading = true;
    this.oneDriveFolderEmpty = false;
    this._oneDriveService.getMsFiles(undefined, undefined, undefined, undefined).subscribe(result => {
      this.oneDriveLoading = false;
      this.oneDriveLoaded = true;
      if (!result.isError && result.responseData) {
        if (result.responseData['item1'] && result.responseData['item1'].length > 0) {
          this.oneDriveItems = result.responseData['item1'];
          this.oneDriveItems.forEach(element => {
            element.isSelected = false;
            element.isShow = true;
            element.isDisabled = this.showFolderOnly && element.extension !== 'folder';
          });
        } else {
          this.oneDriveFolderEmpty = true;
        }
        this.oneDriveItemsFiltered = this.oneDriveItems.filter(i => i.isShow);
        this.oneDriveNextBatchLink = result.responseData['item2'] || '';
      }
    });
  }

  onSearchOneDriveTab(): void {
    const text = this.searchTextOnOneDrive.toLowerCase();
    if (!text) {
      this.oneDriveItemsFiltered = this.oneDriveItems.filter(i => i.isShow);
    } else {
      this.oneDriveItemsFiltered = this.oneDriveItems.filter(i => i.isShow && i.name && i.name.toLowerCase().includes(text));
    }
  }

  selectOneDriveTabItem(item: any): void {
    if (item.isDisabled) return;
    item.isSelected = !item.isSelected;
  }

  getMoreMyFiles(event?: any) {
    if (this.nextBatchLink !== '' && !this.isMoreDataLoading) {
      this.isMoreDataLoading = true;
      this._oneDriveService.getMsFiles(undefined, undefined, this.nextBatchLink, undefined).subscribe(result => {
        this.isMoreDataLoading = false;
        if (!result.isError) {
          if (result.responseData) {
            if (result.responseData['item1'] && result.responseData['item1'].length > 0) {
              this.driveItems.push(...result.responseData['item1']);
              this.driveItems.forEach(element => {
                element.isSelected = false;
                element.isShow = true;
                element.isDisabled = this.showFolderOnly && element.extension !== 'folder';
              });
            }
            if (result.responseData['item2']) {
              this.nextBatchLink = result.responseData['item2'];
            } else {
              this.nextBatchLink = '';
            }
          }
        }
      });
    }
  }

  selectItem(item) {
    if (item.isDisabled) return;
    if (this.isSingleSelect) {
      this.driveItems.forEach(element => {
        element.isSelected = false;
      });
    }
    this.isAllItemsSelected = false;
    item.isSelected = !item.isSelected;
    this.isItemSelected = item.isSelected ? true : false;
  }

  selectAllItems() {
    this.isAllItemsSelected = !this.isAllItemsSelected;
    this.driveItems.forEach(element => {
      if (!element.isDisabled) {
        element.isSelected = this.isAllItemsSelected;
      }
    });
  }

  selectAllOneDriveItems() {
    const enabledItems = this.oneDriveItems.filter(i => !i.isDisabled);
    const allSelected = enabledItems.length > 0 && enabledItems.every(i => i.isSelected);
    enabledItems.forEach(i => i.isSelected = !allSelected);
    this.oneDriveItemsFiltered = this.oneDriveItems.filter(i => i.isShow);
  }

  get isAllOneDriveSelected(): boolean {
    const enabledItems = this.oneDriveItems.filter(i => !i.isDisabled);
    return enabledItems.length > 0 && enabledItems.every(i => i.isSelected);
  }

  selectAllLibraries() {
    this.isAllLibrarySelected = !this.isAllLibrarySelected;
    if (this.selectedSiteData?.drives) {
      this.selectedSiteData.drives.forEach(element => {
        if (!element.isDisabled) {
          element.isSelected = this.isAllLibrarySelected;
        }
      });
    }
  }

  switchToSharePoint(): void {
    this.clearOneDriveSelections();
    this.activeCloudTab = 'sharepoint';
    this.level = 1;
    this.selectedSiteData = null;
    this.selectedDriveData = null;
    this.selectedItem = [];
    this.itemsHistory = [];
    this.currentItemIndex = -1;
    this.isItemSelected = false;
    this.isShowAddItem = false;
    this.isShowCreateOptions = false;
    this.isFolderEmpty = false;
  }

  switchToOneDrive(): void {
    this.activeCloudTab = 'onedrive';
    this.level = 1;
    this.selectedSiteData = null;
    this.selectedDriveData = null;
    this.selectedItem = [];
    this.itemsHistory = [];
    this.currentItemIndex = -1;
    this.isItemSelected = false;
    this.isShowAddItem = false;
    this.isShowCreateOptions = false;
    this.isFolderEmpty = false;
    this.clearOneDriveSelections();
    this.loadOneDriveFilesForTab();
  }

  navigateToBreadcrumb(target: string): void {
    this.isShowAddItem = false;
    switch (target) {
      case 'sites':
        this.level = 1;
        this.selectedSiteData = null;
        this.selectedDriveData = null;
        this.selectedItem = [];
        this.itemsHistory = [];
        this.currentItemIndex = -1;
        this.isItemSelected = false;
        this.isFolderEmpty = false;
        break;
      case 'site':
        this.level = 2;
        this.selectedDriveData = null;
        this.selectedItem = [];
        this.itemsHistory = [];
        this.currentItemIndex = -1;
        this.isItemSelected = false;
        if (this.selectedSiteData?.drives) {
          this.selectedSiteData.drives.forEach(d => d.isSelected = false);
        }
        break;
      case 'drive':
        this.level = 3;
        this.selectedItem = [];
        this.itemsHistory = [];
        this.currentItemIndex = -1;
        this.isItemSelected = false;
        this.isFolderEmpty = false;
        if (this.selectedDriveData?.children) {
          this.selectedDriveData.children.forEach(c => c.isSelected = false);
        }
        break;
      case 'myfiles':
        this.level = 1;
        this.selectedItem = [];
        this.itemsHistory = [];
        this.currentItemIndex = -1;
        this.isItemSelected = false;
        this.isFolderEmpty = false;
        this.oneDriveLoaded = false;
        this.clearOneDriveSelections();
        this.loadOneDriveFilesForTab();
        break;
    }
  }

  navigateToOneDriveFolder(index: number): void {
    this.isShowAddItem = false;
    this.isShowCreateOptions = false;
    this.isFolderEmpty = false;
    const targetItem = this.selectedItem[index];
    this.selectedItem = this.selectedItem.slice(0, index + 1);
    this.itemsHistory = [];
    this.currentItemIndex = -1;
    this.isItemSelected = false;
    this.isSharePointSitesLoading = true;
    this.level = 5;
    this.nextBatchLink = '';
    this._oneDriveService.getMsFiles(targetItem.parentReference.driveId, targetItem.parentReference.driveId === targetItem.id ? undefined : targetItem.id, undefined, undefined).subscribe(result => {
      this.isSharePointSitesLoading = false;
      if (!result.isError && result.responseData) {
        if (result.responseData['item1'] && result.responseData['item1'].length > 0) {
          this.driveItems = result.responseData['item1'];
          this.driveItems.forEach(element => {
            element.isSelected = false;
            element.isShow = true;
            element.isDisabled = this.showFolderOnly && element.extension !== 'folder';
          });
        } else {
          this.driveItems = [];
          this.isFolderEmpty = true;
        }
        this.setItems(this.driveItems);
        this.nextBatchLink = result.responseData['item2'] || '';
      }
    });
  }

  navigateToSharePointFolder(index: number): void {
    this.isShowAddItem = false;
    this.isShowCreateOptions = false;
    this.isFolderEmpty = false;
    const targetItem = this.selectedItem[index];
    this.selectedItem = this.selectedItem.slice(0, index + 1);
    this.itemsHistory = [];
    this.currentItemIndex = -1;
    this.isItemSelected = false;
    this.isSharePointSitesLoading = true;
    this.level = 5;
    this.nextBatchLink = '';
    this._oneDriveService.getMsFiles(targetItem.parentReference.driveId, targetItem.parentReference.driveId === targetItem.id ? undefined : targetItem.id, undefined, undefined).subscribe(result => {
      this.isSharePointSitesLoading = false;
      if (!result.isError && result.responseData) {
        if (result.responseData['item1'] && result.responseData['item1'].length > 0) {
          this.driveItems = result.responseData['item1'];
          this.driveItems.forEach(element => {
            element.isSelected = false;
            element.isShow = true;
            element.isDisabled = this.showFolderOnly && element.extension !== 'folder';
          });
        } else {
          this.driveItems = [];
          this.isFolderEmpty = true;
        }
        this.setItems(this.driveItems);
        this.nextBatchLink = result.responseData['item2'] || '';
      }
    });
  }

  get hasOneDriveSelection(): boolean {
    return this.oneDriveItems.some(i => i.isSelected);
  }

  get oneDriveSelectedCount(): number {
    return this.oneDriveItems.filter(i => i.isSelected).length;
  }

  clearOneDriveSelections(): void {
    this.oneDriveItems.forEach(i => i.isSelected = false);
  }

  get currentSelectedCount(): number {
    if (this.level === 1 && this.activeCloudTab === 'onedrive') {
      return this.oneDriveSelectedCount;
    } else if (this.level === 2) {
      return this.selectedSiteData?.drives?.filter(x => x.isSelected).length || 0;
    } else if (this.level === 3) {
      return this.selectedDriveData?.children?.filter(x => x.isSelected).length || 0;
    } else {
      return this.driveItems?.filter(x => x.isSelected).length || 0;
    }
  }

  selectOneDriveItemsFromTab(): void {
    const selected = this.oneDriveItems.filter(i => i.isSelected);
    if (selected.length === 0) { return; }
    this.selectedItems.emit([selected, null]);
    this.clickClose();
  }

  selectItemAndSave(isOneDrive: boolean) {
    if (this.isSaving || Date.now() - this._lastSelectClick < 1000) return;
    this._lastSelectClick = Date.now();
    this.isSaving = true;
    this.noOfSelectedItems = this.driveItems.filter(x => x.isSelected).length;
    if (this.noOfSelectedItems === 0) {
      this.isSaving = false;
      let options = this.toastrOptions;
      this.toastr
        .error('Please select at least one item.',
          null, options);
      return;
    }
    const checkedFiles = this.driveItems.filter(x => x.isSelected);
    this.selectedItems.emit([checkedFiles, null, this.displayTrailFor(checkedFiles)]);
    this.clickClose();
  }

  createLinkToItems(item, isOneDrive, noOfSelectedItems): void {
    let input: GroupLinkDto = new GroupLinkDto();

    input.type = 3; // 3 for shortcut
    input.name = item.name;
    if (!item.webUrl.startsWith('http')) {
      input.url = 'http://' + item.webUrl;
    } else {
      input.url = item.webUrl;
    }
    input.isShareFile = isOneDrive; // this.oneDrivePath ? true : false;
    input.isEveryone = true;
    // input.path = this.groupLinkObj.path;
    input.keyId = item.id;
    input.extension = item.extension;
    input.driveCategoryId = this.driveCategoryId;

    input.driveId = item.parentReference.driveId;
    input.siteId = item.parentReference.siteId;
    this._linkService.createOrUpdateGroupLink(input).subscribe(result => {
      if (!result.isError) {
        if (noOfSelectedItems === 1) {
          // this.dataService.refreshFilesSlider.next();
          let options = this.toastrOptions;
          this.toastr
            .success('Added to team drive.',
              null, options);
          this.clickClose();
          this.isSaving = false;
        }
      } else {
        if (result.errorMessage.toLocaleLowerCase() === 'duplicate') {
          this.isSaving = false;
          let options = this.toastrOptions;
          this.toastr
            .error('Item already added to team drive.',
              null, options);
        } else {
          this.isSaving = false;
          let options = this.toastrOptions;
          this.toastr
            .error('Error while adding to team drive.',
              null, options);
        }
      }
    });
  }

  selectAllDrives() {
    this.isAllDrivesSelected = !this.isAllDrivesSelected;
    if (this.selectedDriveData.children.length > 0) {
      this.selectedDriveData.children.forEach(element => {
        if (!element.isDisabled) {
          element.isSelected = this.isAllDrivesSelected;
        }
      });
    }
  }

  selectDriveItem(item) {
    if (item.isDisabled) return;
    if (this.isSingleSelect) {
      this.selectedDriveData.children.forEach(element => {
        element.isSelected = false;
      });
    }
    this.isAllItemsSelected = false;
    item.isSelected = !item.isSelected;
    this.isItemSelected = item.isSelected ? true : false;
  }

  selectDriveItemAndSave() {
    if (this.isSaving || Date.now() - this._lastSelectClick < 1000) return;
    this._lastSelectClick = Date.now();
    this.isSaving = true;
    this.noOfSelectedItems = this.selectedDriveData.children.filter(x => x.isSelected).length;
    if (this.noOfSelectedItems === 0) {
      this.isSaving = false;
      let options = this.toastrOptions;
      this.toastr
        .error('Please select at least one item.',
          null, options);
      return;
    }
    let tmp = this.selectedDriveData.children.filter(x => x.isSelected);
    this.selectedItems.emit([tmp, null, this.displayTrailFor(tmp)]);
    this.clickClose();
  }

  /**
   * The display-name trail this popup's own breadcrumb is showing: site > library > folders.
   * Emitted as a third element alongside the selection (existing receivers read only [0] and [1],
   * so this is additive) because a consumer that rebuilds the path from the item's webUrl gets the
   * library's URL segment - "Shared Documents" - where the user picked and expects "Documents".
   */
  private currentDisplayTrail(): string[] {
    const trail: string[] = [];
    // Sites carry the human label on displayName (name is the URL-safe form: "Ava3" for "Ava 3");
    // this is what the popup's own breadcrumb binds to. Drives and folders use name.
    const siteLabel = this.selectedSiteData && (this.selectedSiteData.displayName || this.selectedSiteData.name);
    if (siteLabel) { trail.push(siteLabel); }
    if (this.selectedDriveData && this.selectedDriveData.name) { trail.push(this.selectedDriveData.name); }
    (this.selectedItem || []).forEach(crumb => {
      if (crumb && crumb.name) { trail.push(crumb.name); }
    });
    return trail;
  }

  /**
   * Display-name trail for a single emitted item: this popup's breadcrumb plus the item itself.
   * Returns [] for multi-select (no one location to describe) and outside SharePoint (OneDrive, or a
   * folder opened directly), where there is no site/library context - receivers then keep their
   * existing webUrl-derived path.
   */
  private displayTrailFor(items: any[]): string[] {
    if (!items || items.length !== 1) { return []; }
    if (!this.selectedSiteData || !(this.selectedSiteData.displayName || this.selectedSiteData.name)) { return []; }
    const trail = this.currentDisplayTrail();
    const name = items[0] && (items[0].name || items[0].displayName);
    if (name && trail[trail.length - 1] !== name) { trail.push(name); }
    return trail;
  }

  /**
   * Selects the current folder/location the user is browsing in.
   * If items are checked, it selects those instead.
   */
  selectCurrentLocation(): void {
    if (this.isSaving || Date.now() - this._lastSelectClick < 1000) return;
    this._lastSelectClick = Date.now();

    // Level 0: folder opened directly via folderDetails
    if (this.level === 0) {
      const checkedItems = this.driveItems.filter(x => x.isSelected);
      if (checkedItems.length > 0) {
        this.selectedItems.emit([checkedItems, null, this.displayTrailFor(checkedItems)]);
      } else if (this.folderDetails) {
        // Emit the folder itself as the selected item
        this.selectedItems.emit([[{
          id: this.folderDetails.itemId,
          name: this.folderDetails.folderName || 'Current folder',
          parentReference: { driveId: this.folderDetails.driveId, driveType: this.folderDetails.isOneDrive ? 'business' : 'documentLibrary' },
          webUrl: this.folderDetails.webUrl || '',
          extension: 'folder'
        }], null]);
      }
      this.clickClose();
      return;
    }

    // Level 1 - OneDrive tab
    if (this.level === 1 && this.activeCloudTab === 'onedrive') {
      const checkedItems = this.oneDriveItems.filter(i => i.isSelected);
      if (checkedItems.length > 0) {
        this.selectedItems.emit([checkedItems, null, this.displayTrailFor(checkedItems)]);
      } else if (this.oneDriveItems.length > 0) {
        // Select OneDrive root
        const firstItem = this.oneDriveItems[0];
        this.selectedItems.emit([[{
          id: null,
          name: 'OneDrive',
          parentReference: { driveId: firstItem.parentReference?.driveId, driveType: 'business' },
          webUrl: '',
          extension: 'folder'
        }], null]);
      }
      this.clickClose();
      return;
    }

    // Level 1 - SharePoint tab (sites list — no current folder)
    if (this.level === 1 && this.activeCloudTab === 'sharepoint') {
      return;
    }

    // Level 2: Libraries list — select checked libraries or the current site
    if (this.level === 2) {
      const checkedDrives = this.selectedSiteData?.drives?.filter(x => x.isSelected) || [];
      if (checkedDrives.length > 0) {
        this.selectedItems.emit([checkedDrives, this.selectedSiteData.id, this.displayTrailFor(checkedDrives)]);
      }
      this.clickClose();
      return;
    }

    // Level 3: Inside a library — select checked items or the library itself
    if (this.level === 3) {
      const checkedItems = this.selectedDriveData?.children?.filter(x => x.isSelected) || [];
      if (checkedItems.length > 0) {
        this.selectedItems.emit([checkedItems, null, this.displayTrailFor(checkedItems)]);
      } else if (this.selectedDriveData) {
        // Select the library/drive itself. Emit the parent site's id and tag
        // the item with extension: 'documentLibrary' so receivers (e.g.
        // setAttachmentInput's fallback / shortcuts isDocLibrary check) can
        // resolve fileType to 'documentLibrary' instead of 'otherfile'.
        const libraryItem = {
          ...this.selectedDriveData,
          extension: 'documentLibrary'
        };
        this.selectedItems.emit([[libraryItem], this.selectedSiteData?.id ?? null, this.displayTrailFor([libraryItem])]);
      }
      this.clickClose();
      return;
    }

    // Level 4+: Inside a subfolder — select checked items or the current folder
    if (this.level >= 4) {
      const checkedItems = this.driveItems.filter(x => x.isSelected);
      if (checkedItems.length > 0) {
        this.selectedItems.emit([checkedItems, null, this.displayTrailFor(checkedItems)]);
      } else if (this.selectedItem.length > 0) {
        // Select the current folder the user is inside
        this.selectedItems.emit([[this.selectedItem[this.selectedItem.length - 1]], null, this.displayTrailFor([this.selectedItem[this.selectedItem.length - 1]])]);
      }
      this.clickClose();
      return;
    }
  }

  get canSelectCurrentLocation(): boolean {
    if (this.level === 1 && this.activeCloudTab === 'sharepoint') return false;
    if (this.level === 2) return this.currentSelectedCount > 0;
    return true;
  }

  get currentLocationName(): string {
    if (this.level === 0 && this.folderDetails) {
      return this.folderDetails.folderName || 'Current folder';
    }
    if (this.level === 1 && this.activeCloudTab === 'onedrive') {
      return 'OneDrive';
    }
    if (this.level === 3 && this.selectedDriveData) {
      return this.selectedDriveData.name;
    }
    if (this.level >= 4 && this.selectedItem.length > 0) {
      return this.selectedItem[this.selectedItem.length - 1].name;
    }
    return '';
  }

  applySearchOnLevel2() {
    if (this.selectedSiteData.drives.length > 0) {
      this.selectedSiteData.drives.forEach(element => {
        if (element.name.toLowerCase().indexOf(this.searchTextOnL2.toLowerCase()) > -1) {
          element.isShow = true;
        } else {
          element.isShow = false;
        }
      });
      this.checkIfItemsToShowOnLevel2();
    } else {
      this.noResultFound = false;
    }
  }

  private checkIfItemsToShowOnLevel2() {
    let noOfIsShowItems = this.selectedSiteData.drives.filter(x => x.isShow).length;
    if (noOfIsShowItems === 0) {
      this.noResultFound = true;
    } else {
      this.noResultFound = false;
    }
  }

  removeSearchOnLevel2() {
    this.searchTextOnL2 = '';
    if (this.selectedSiteData.drives.length > 0) {
      this.selectedSiteData.drives.forEach(element => {
        element.isShow = true;
      });
      this.checkIfItemsToShowOnLevel2();
    } else {
      this.noResultFound = false;
    }
  }

  applySearchOnLevel3() {
    if (this.selectedDriveData.children.length > 0) {
      this.selectedDriveData.children.forEach(element => {
        if (element.name.toLowerCase().indexOf(this.searchTextOnL3.toLowerCase()) > -1) {
          element.isShow = true;
        } else {
          element.isShow = false;
        }
      });
      this.checkIfItemsToShowOnLevel3();
    } else {
      this.noResultFound = false;
    }
  }

  private checkIfItemsToShowOnLevel3() {
    let noOfIsShowItems = this.selectedDriveData.children.filter(x => x.isShow).length;
    if (noOfIsShowItems === 0) {
      this.noResultFound = true;
    } else {
      this.noResultFound = false;
    }
  }

  removeSearchOnLevel3() {
    this.searchTextOnL3 = '';
    if (this.selectedDriveData.children.length > 0) {
      this.selectedDriveData.children.forEach(element => {
        element.isShow = true;
      });
      this.checkIfItemsToShowOnLevel3();
    } else {
      this.noResultFound = false;
    }
  }

  applySearchOnLevel4() {
    if (this.driveItems.length > 0) {
      this.driveItems.forEach(element => {
        if (element.name.toLowerCase().indexOf(this.searchTextOnL4.toLowerCase()) > -1) {
          element.isShow = true;
        } else {
          element.isShow = false;
        }
      });
      this.checkIfItemsToShowOnLevel4();
    } else {
      this.noResultFound = false;
    }
  }

  private checkIfItemsToShowOnLevel4() {
    let noOfIsShowItems = this.driveItems.filter(x => x.isShow).length;
    if (noOfIsShowItems === 0) {
      this.noResultFound = true;
    } else {
      this.noResultFound = false;
    }
  }

  removeSearchOnLevel4() {
    this.searchTextOnL4 = '';
    if (this.driveItems.length > 0) {
      this.driveItems.forEach(element => {
        element.isShow = true;
      });
      this.checkIfItemsToShowOnLevel4();
    } else {
      this.noResultFound = false;
    }
  }

  applySearchOnLevel5() {
    if (this.driveItems.length > 0) {
      this.driveItems.forEach(element => {
        if (element.name.toLowerCase().indexOf(this.searchTextOnL5.toLowerCase()) > -1) {
          element.isShow = true;
        } else {
          element.isShow = false;
        }
      });
      this.checkIfItemsToShowOnLevel5();
    } else {
      this.noResultFound = false;
    }
  }

  private checkIfItemsToShowOnLevel5() {
    let noOfIsShowItems = this.driveItems.filter(x => x.isShow).length;
    if (noOfIsShowItems === 0) {
      this.noResultFound = true;
    } else {
      this.noResultFound = false;
    }
  }

  removeSearchOnLevel5() {
    this.searchTextOnL5 = '';
    if (this.driveItems.length > 0) {
      this.driveItems.forEach(element => {
        element.isShow = true;
      });
      this.checkIfItemsToShowOnLevel5();
    } else {
      this.noResultFound = false;
    }
  }

  applySearchOnLevel0() {
    if (this.driveItems.length > 0) {
      this.driveItems.forEach(element => {
        if (element.name.toLowerCase().indexOf(this.searchTextOnL0.toLowerCase()) > -1) {
          element.isShow = true;
        } else {
          element.isShow = false;
        }
      });
      this.checkIfItemsToShowOnLevel0();
    } else {
      this.noResultFound = false;
    }
  }

  private checkIfItemsToShowOnLevel0() {
    let noOfIsShowItems = this.driveItems.filter(x => x.isShow).length;
    if (noOfIsShowItems === 0) {
      this.noResultFound = true;
    } else {
      this.noResultFound = false;
    }
  }

  removeSearchOnLevel0() {
    this.searchTextOnL0 = '';
    if (this.driveItems.length > 0) {
      this.driveItems.forEach(element => {
        element.isShow = true;
      });
      this.checkIfItemsToShowOnLevel0();
    } else {
      this.noResultFound = false;
    }
  }


  applySearchOnLevel1() {
    if (this.searchTextOnL1.trim() !== '' && this.searchTextOnL1.length > 2) {
      this.onSearching.next();
    } else if (this.searchTextOnL1.trim() === '') {
      this.noSharePointSiteResultFound = false;
      this.getSharepointSites();
    }
  }

  removeSearchOnLevel1() {
    this.searchTextOnL1 = '';
    this.noSharePointSiteResultFound = false;
    this.getSharepointSites();
  }

  onNewLibKeyPress(event: any): boolean {
    if (event.keyCode === 13) {
      let el = document.querySelector('.create-library') as HTMLElement;
      let value: string = el ? el.innerText.trim() : '';
      if (value.length > 0) {
        console.log('Pressing library with name: ' + value);
        this.createLibrary(value);
      }
    }
    return event.keyCode !== 13;
  }

  onNewLibBlur() {
    let el = document.querySelector('.create-library') as HTMLElement;
    let value: string = el ? el.innerText.trim() : '';
    if (value.length === 0) {
      this.isShowAddLibrary = false;
      return;
    }
    console.log('Blurring library with name: ' + value);
    this.createLibrary(value);
  }

  private createLibrary(libraryName: string) {
    let value: string = libraryName.trim();
    let len = value.length;
    if (len === 0) {
      return;
    }
    this.isSaving = true;
    let input: CreateFolderDto = new CreateFolderDto();
    input.name = value;
    input.siteId = this.selectedSiteData.id;
    let el = document.querySelector('.create-library') as HTMLElement;
    if (el) el.innerText = '';
    this.isShowAddLibrary = false;
    // Optimistic insert FIRST so the user sees the new library immediately.
    // The real driveId/folderUrl get patched in once
    // createDocumentLibrary responds, or the placeholder is removed on error.
    const ts = Date.now();
    const tempLib: any = {
      id: 'pending_' + ts,
      _tempId: 'temp_lib_' + ts + '_' + Math.random().toString(36).substr(2, 9),
      name: value,
      siteId: this.selectedSiteData.id,
      webUrl: '',
      isSelected: false,
      isShow: true,
      children: [],
      isPending: true
    };
    if (!this.selectedSiteData.drives) this.selectedSiteData.drives = [];
    this.selectedSiteData.drives.unshift(tempLib);
    this.isFolderEmpty = false;
    this._oneDriveService.createDocumentLibrary(input).subscribe(result => {
      this.isSaving = false;
      if (!result.isError && result.responseData) {
        const r: any = result.responseData;
        const item = this.selectedSiteData.drives.find((d: any) => d._tempId === tempLib._tempId);
        if (item) {
          if (r.driveId) item.id = r.driveId;
          if (r.folderUrl) item.webUrl = r.folderUrl;
          if (r.siteId) item.siteId = r.siteId;
          item.isPending = false;
        }
        let options = this.toastrOptions;
        this.toastr
          .success('Library created successfully.',
            null, options);
      } else {
        const idx = this.selectedSiteData.drives.findIndex((d: any) => d._tempId === tempLib._tempId);
        if (idx > -1) this.selectedSiteData.drives.splice(idx, 1);
        let options = this.toastrOptions;
        this.toastr
          .error('Error while creating library.',
            null, options);
      }
    });
  }

  showAddLibrary() {
    this.isShowCreateOptions = false;
    this.isShowAddItem = false;
    this.isShowAddLibrary = true;
    setTimeout(() => {
      const el = document.querySelector('.create-library') as HTMLElement;
      if (el) el.focus();
    }, 100);
  }

  toggleCreateOptions() {
    this.isShowCreateOptions = !this.isShowCreateOptions;
    if (this.isShowCreateOptions) {
      this.isShowAddItem = false;
      this.documentName = '';
    }
  }

  showAddItem(type) {
    this.isShowCreateOptions = false;
    this.isShowAddItem = true;
    this.selectedDocType = type;
    this.documentName = '';
    setTimeout(() => {
      const input = document.querySelector('.file-selector-create-input') as HTMLInputElement;
      if (input) input.focus();
    }, 100);
  }

  selectDocType(type) {
    this.selectedDocType = type;
  }

  autoFillTitle(): void {
    this.documentName = '';
    this.documentName = this.documentName !== '' ? this.documentName + ' ' + this.sanitizeFilename(this.folderDetails.projectName) : this.sanitizeFilename(this.folderDetails.projectName);
    if (this.selectedDocType === 5) {
      $('#create-folder-input').val(this.documentName);
    }
    if (this.selectedDocType !== 5) {
      $('#create-file-input').val(this.documentName);
    }
    setTimeout(() => {
      if (this.selectedDocType === 5) {
        $('#create-folder-input').trigger('blur');
      } else {
        $('#create-file-input').trigger('blur');
      }
    }, 50);
  }

  onNewFileKeyPress(event: any, isOneDrive, level): boolean {
    if (event.keyCode === 13) {
      if (level === 0) {
        this.createFileOnLevel0(event, isOneDrive, level);
      } else {
        this.createFile(event, isOneDrive, level);
      }
    }
    return event.keyCode !== 13;
  }

  onNewFileBlur(event: any, isOneDrive, level) {
    setTimeout(() => {
      let value: string = this.documentName.trim();
      if (value.length > 0) {
        if (level === 0) {
          this.createFileOnLevel0(event, isOneDrive, level);
        } else {
          this.createFile(event, isOneDrive, level);
        }
      } else {
        this.isShowAddItem = false;
      }
    }, 500);
  }

  createFileOnLevel0(event, isOneDrive, level): void {
    let value: string = this.documentName.trim();
    let len = value.length;
    if (len === 0) {
      return;
    }
    if (this.isSaving) {
      return;
    }
    let input: CreateFileInputDto = new CreateFileInputDto();
    input.isShareFile = true;
    input.isOneNote = this.selectedDocType === 4 ? true : false;

    input.driveId = this.folderDetails.driveId;
    input.itemId = this.folderDetails.itemId;
    // if (isOneDrive) {
    //   input.itemId = this.selectedItem.length > 1 ? this.selectedItem[this.selectedItem.length - 1].id : '';
    //   input.driveId = this.selectedItem.length > 1 ? this.selectedItem[this.selectedItem.length - 1].parentReference.driveId : '';
    // } else {
    //   if (level === 3) {
    //     // SharePoint Drive level
    //     input.driveId = this.selectedDriveData ? this.selectedDriveData.id : '';
    //   } else {
    //     // SharePoint Items level
    //     input.itemId = this.selectedItem.length > 0 ? this.selectedItem[this.selectedItem.length - 1].id : '';
    //     input.driveId = this.selectedItem.length > 0 ? this.selectedItem[this.selectedItem.length - 1].parentReference.driveId : '';
    //   }
    // }
    input.name = value.trim();
    if (this.selectedDocType === 1) {
      input.extension = '.docx';
    } else if (this.selectedDocType === 2) {
      input.extension = '.xlsx';
    } else if (this.selectedDocType === 3) {
      input.extension = '.pptx';
    }
    this.isSaving = true;
    this.documentName = '';
    this.isShowAddItem = false;
    if (input.itemId === input.driveId) { input.itemId = undefined; }
    // Optimistic insert FIRST so the user sees the new file at the top
    // immediately. After the API responds we patch the same row with the
    // real id/keyId/url, or remove it on failure.
    const placeholder = this.buildPlaceholderFileItem(input);
    this.insertOptimisticItemForLevel(placeholder, level);
    const tempId = placeholder._tempId;
    this._oneDriveService.createOneDriveFile(input).subscribe(result => {
      this.isSaving = false;
      if (!result.isError && result.responseData) {
        const item = this.findOptimisticItemByTempId(tempId, level);
        this.applyFileResponseToItem(item, result.responseData);
      } else {
        this.removeOptimisticItem(tempId, level);
        if (result.errorMessage) {
          const errorDef = AppConsts.oneDriveErrors[result.errorMessage] ?? { message: result.errorMessage };
          this.fsShellError = { title: "Couldn't create this file", ...errorDef };
          this._fsLastErrorCode = result.errorMessage;
          if (errorDef?.saveLabel) { this._fsPendingFileInput = input; this._fsPendingLevel = level; this._fsPendingIsOneDrive = false; }
          this.fsErrorShell.show();
        }
      }
    });
  }

  docNameChange(): void {
    this.fileNameError = false;
    this.fileNameErrorMessage = '';
  }

  createFile(event, isOneDrive, level): void {
    let value: string = this.documentName.trim();
    let len = value.length;
    if (len === 0) {
      return;
    }
    if (this.isSaving) {
      return;
    }
    let input: CreateFileInputDto = new CreateFileInputDto();
    input.isShareFile = true;
    input.isOneNote = this.selectedDocType === 4 ? true : false;
    if (isOneDrive) {
      if (this.selectedItem.length > 1) {
        input.itemId = this.selectedItem[this.selectedItem.length - 1].id;
        input.driveId = this.selectedItem[this.selectedItem.length - 1].parentReference.driveId;
      } else if (this.oneDriveItems.length > 0) {
        input.driveId = this.oneDriveItems[0].parentReference?.driveId || '';
        input.itemId = '';
      }
    } else {
      if (level === 3) {
        // SharePoint Drive level
        input.driveId = this.selectedDriveData ? this.selectedDriveData.id : '';
      } else {
        // SharePoint Items level
        input.itemId = this.selectedItem.length > 0 ? this.selectedItem[this.selectedItem.length - 1].id : '';
        input.driveId = this.selectedItem.length > 0 ? this.selectedItem[this.selectedItem.length - 1].parentReference.driveId : '';
      }
    }
    input.name = value.trim();
    if (this.selectedDocType === 1) {
      input.extension = '.docx';
    } else if (this.selectedDocType === 2) {
      input.extension = '.xlsx';
    } else if (this.selectedDocType === 3) {
      input.extension = '.pptx';
    }
    this.isSaving = true;
    this.documentName = '';
    this.isShowAddItem = false;
    if (input.itemId === input.driveId) { input.itemId = undefined; }
    // Optimistic insert FIRST so the user sees the new file at the top
    // immediately. The id/url get patched in once createOneDriveFile responds.
    const placeholder = this.buildPlaceholderFileItem(input);
    this.insertOptimisticItemForLevel(placeholder, level, isOneDrive);
    const tempId = placeholder._tempId;
    this._oneDriveService.createOneDriveFile(input).subscribe(result => {
      this.isSaving = false;
      if (!result.isError && result.responseData) {
        const item = this.findOptimisticItemByTempId(tempId, level);
        this.applyFileResponseToItem(item, result.responseData);
      } else {
        this.removeOptimisticItem(tempId, level);
        if (result.errorMessage) {
          const errorDef = AppConsts.oneDriveErrors[result.errorMessage] ?? { message: result.errorMessage };
          this.fsShellError = { title: "Couldn't create this file", ...errorDef };
          this._fsLastErrorCode = result.errorMessage;
          if (errorDef?.saveLabel) { this._fsPendingFileInput = input; this._fsPendingLevel = level; this._fsPendingIsOneDrive = isOneDrive; }
          this.fsErrorShell.show();
        }
      }
    });
  }

  onNewFolderKeyPress(event: any, isOneDrive, level): boolean {
    if (event.keyCode === 13) {
      if (level === 0) {
        this.createFolderOnLevel0(event, isOneDrive, level);
      } else {
        this.createFolder(event, isOneDrive, level);
      }
    }
    return event.keyCode !== 13;
  }

  onNewFolderBlur(event: any, isOneDrive, level) {
    setTimeout(() => {
      let value: string = this.documentName.trim();
      if (value.length > 0) {
        if (level === 0) {
          this.createFolderOnLevel0(event, isOneDrive, level);
        } else {
          this.createFolder(event, isOneDrive, level);
        }
      } else {
        this.isShowAddItem = false;
      }
    }, 500);
  }

  createFolderOnLevel0(event, isOneDrive, level): void {
    let value: string = this.documentName.trim();
    let len = value.length;
    if (len === 0) {
      return;
    }
    if (this.isSaving) {
      return;
    }
    let input: CreateFolderDto = new CreateFolderDto();
    input.isDocLibrary = false;
    input.isRename = false;
    input.driveId = this.folderDetails.driveId;
    input.itemId = this.folderDetails.itemId;

    input.name = value.trim();
    input.folderType = this.selectedItem.length === 1 ? 1 : 3;
    this.isSaving = true;
    this.documentName = '';
    this.isShowAddItem = false;
    // Optimistic insert FIRST so the user sees the new folder immediately.
    // Patched with the real folderId/folderUrl after createFolder responds.
    const placeholder = this.buildPlaceholderFolderItem(input, false, 'documentLibrary');
    this.insertOptimisticItemForLevel(placeholder, level);
    const tempId = placeholder._tempId;
    this._oneDriveService.createFolder(input).subscribe(result => {
      this.isSaving = false;
      if (!result.isError && result.responseData) {
        const item = this.findOptimisticItemByTempId(tempId, level);
        this.applyFolderResponseToItem(item, result.responseData);
      } else {
        this.removeOptimisticItem(tempId, level);
        if (result.errorMessage) {
          const errorDef = AppConsts.oneDriveErrors[result.errorMessage] ?? { message: result.errorMessage };
          this.fsShellError = { title: "Couldn't create this folder", ...errorDef };
          this._fsLastErrorCode = result.errorMessage;
          if (errorDef?.saveLabel) { this._fsPendingFolderInput = input; this._fsPendingLevel = level; this._fsPendingIsOneDrive = false; this._fsPendingFolderPlaceholderType = 'documentLibrary'; }
          this.fsErrorShell.show();
        }
      }
    });
  }

  createFolder(event, isOneDrive, level): void {
    let value: string = this.documentName.trim();
    let len = value.length;
    if (len === 0) {
      return;
    }
    if (this.isSaving) {
      return;
    }
    let input: CreateFolderDto = new CreateFolderDto();
    input.isDocLibrary = false;
    input.isRename = false;
    if (isOneDrive) {
      if (this.selectedItem.length > 1) {
        input.itemId = this.selectedItem[this.selectedItem.length - 1].id;
        input.driveId = this.selectedItem[this.selectedItem.length - 1].parentReference.driveId;
      } else if (this.oneDriveItems.length > 0) {
        input.driveId = this.oneDriveItems[0].parentReference?.driveId || '';
        input.itemId = '';
      }
    } else {
      if (level === 3) {
        // SharePoint Drive level
        input.driveId = this.selectedDriveData ? this.selectedDriveData.id : '';
      } else {
        // SharePoint Items level
        input.itemId = this.selectedItem.length > 0 ? this.selectedItem[this.selectedItem.length - 1].id : '';
        input.driveId = this.selectedItem.length > 0 ? this.selectedItem[this.selectedItem.length - 1].parentReference.driveId : '';
      }
    }
    input.name = value.trim();
    input.folderType = this.selectedItem.length === 1 ? 1 : 3;
    this.isSaving = true;
    this.documentName = '';
    this.isShowAddItem = false;
    // Optimistic insert FIRST. The real folderId/folderUrl get patched in once
    // createFolder responds, or the placeholder is removed on failure.
    const placeholder = this.buildPlaceholderFolderItem(input, false, isOneDrive ? 'business' : 'documentLibrary');
    this.insertOptimisticItemForLevel(placeholder, level, isOneDrive);
    const tempId = placeholder._tempId;
    this._oneDriveService.createFolder(input).subscribe(result => {
      this.isSaving = false;
      if (!result.isError && result.responseData) {
        const item = this.findOptimisticItemByTempId(tempId, level);
        this.applyFolderResponseToItem(item, result.responseData);
      } else {
        this.removeOptimisticItem(tempId, level);
        if (result.errorMessage) {
          const errorDef = AppConsts.oneDriveErrors[result.errorMessage] ?? { message: result.errorMessage };
          this.fsShellError = { title: "Couldn't create this folder", ...errorDef };
          this._fsLastErrorCode = result.errorMessage;
          if (errorDef?.saveLabel) { this._fsPendingFolderInput = input; this._fsPendingLevel = level; this._fsPendingIsOneDrive = isOneDrive; this._fsPendingFolderPlaceholderType = isOneDrive ? 'business' : 'documentLibrary'; }
          this.fsErrorShell.show();
        }
      }
    });
  }

  okInfoPopupClicked(ev: any) {
    this.isShowInfoPopup = false;
  }

  fsCloseError(): void {
    this.fsErrorShell.hide();
    this.fsShellError = undefined;
    this._fsLastErrorCode = null;
    this._fsPendingFileInput = null;
    this._fsPendingFolderInput = null;
  }

  fsSaveError(): void {
    this.fsErrorShell.hide();
    this.fsShellError = undefined;
    if (this._fsPendingFolderInput) {
      const input = this._fsPendingFolderInput;
      const level = this._fsPendingLevel;
      const isOneDrive = this._fsPendingIsOneDrive;
      const placeholderType = this._fsPendingFolderPlaceholderType;
      this._fsPendingFolderInput = null;
      if (this._fsLastErrorCode === '101') {
        this.dataService.openRenameFileModal.next({ fileName: input.name, skipRenameCall: true, extension: 'folder' });
        this.dataService.copyMoveRenameResult.pipe(take(1)).subscribe(newName => {
          if (!newName) { return; }
          input.name = newName;
          input.isUseExisting = false;
          this._fsDoCreateFolder(input, level, isOneDrive, placeholderType);
        });
      } else if (this._fsLastErrorCode === '106' || this._fsLastErrorCode === '107' || this._fsLastErrorCode === '114') {
        input.isUseExisting = false;
        this._fsDoCreateFolder(input, level, isOneDrive, placeholderType);
      }
      return;
    }
    if (this._fsPendingFileInput) {
      const input = this._fsPendingFileInput;
      const level = this._fsPendingLevel;
      const isOneDrive = this._fsPendingIsOneDrive;
      this._fsPendingFileInput = null;
      if (this._fsLastErrorCode === '101') {
        this.dataService.openRenameFileModal.next({
          fileName: input.name + (input.extension || ''),
          skipRenameCall: true,
        });
        this.dataService.copyMoveRenameResult.pipe(take(1)).subscribe(newName => {
          if (!newName) { return; }
          const ext = input.extension || '';
          input.name = (ext && newName.endsWith(ext)) ? newName.slice(0, -ext.length) : newName;
          input.duplicateAction = 0;
          this._fsDoCreateFile(input, level, isOneDrive);
        });
      } else if (this._fsLastErrorCode === '106' || this._fsLastErrorCode === '107' || this._fsLastErrorCode === '114') {
        input.duplicateAction = 0;
        this._fsDoCreateFile(input, level, isOneDrive);
      }
      return;
    }
  }

  private _fsDoCreateFile(input: CreateFileInputDto, level: number, isOneDrive: boolean): void {
    this.isSaving = true;
    const placeholder = this.buildPlaceholderFileItem(input);
    if (isOneDrive) {
      this.insertOptimisticItemForLevel(placeholder, level, isOneDrive);
    } else {
      this.insertOptimisticItemForLevel(placeholder, level);
    }
    const tempId = placeholder._tempId;
    this._oneDriveService.createOneDriveFile(input).subscribe(result => {
      this.isSaving = false;
      if (!result.isError && result.responseData) {
        const item = this.findOptimisticItemByTempId(tempId, level);
        this.applyFileResponseToItem(item, result.responseData);
      } else {
        this.removeOptimisticItem(tempId, level);
        if (result.errorMessage) {
          const errorDef = AppConsts.oneDriveErrors[result.errorMessage] ?? { message: result.errorMessage };
          this.fsShellError = { title: "Couldn't create this file", ...errorDef };
          this._fsLastErrorCode = result.errorMessage;
          if (errorDef?.saveLabel) { this._fsPendingFileInput = input; this._fsPendingLevel = level; this._fsPendingIsOneDrive = isOneDrive; }
          this.fsErrorShell.show();
        }
      }
    });
  }

  private _fsDoCreateFolder(input: CreateFolderDto, level: number, isOneDrive: boolean, placeholderType: string): void {
    this.isSaving = true;
    const placeholder = this.buildPlaceholderFolderItem(input, false, placeholderType);
    if (isOneDrive) {
      this.insertOptimisticItemForLevel(placeholder, level, isOneDrive);
    } else {
      this.insertOptimisticItemForLevel(placeholder, level);
    }
    const tempId = placeholder._tempId;
    this._oneDriveService.createFolder(input).subscribe(result => {
      this.isSaving = false;
      if (!result.isError && result.responseData) {
        const item = this.findOptimisticItemByTempId(tempId, level);
        this.applyFolderResponseToItem(item, result.responseData);
      } else {
        this.removeOptimisticItem(tempId, level);
        if (result.errorMessage) {
          const errorDef = AppConsts.oneDriveErrors[result.errorMessage] ?? { message: result.errorMessage };
          this.fsShellError = { title: "Couldn't create this folder", ...errorDef };
          this._fsLastErrorCode = result.errorMessage;
          if (errorDef?.saveLabel) { this._fsPendingFolderInput = input; this._fsPendingLevel = level; this._fsPendingIsOneDrive = isOneDrive; this._fsPendingFolderPlaceholderType = placeholderType; }
          this.fsErrorShell.show();
        }
      }
    });
  }

  refreshItemsOnL0(item) {
    this.isFolderEmpty = false;
    this.isSharePointSitesLoading = true;
    this.nextBatchLink = '';
    this._oneDriveService.getMsFiles(item.driveId ? item.driveId : undefined, item.itemId ? item.itemId : undefined, undefined, undefined).subscribe(result => {
      this.isSharePointSitesLoading = false;
      if (!result.isError) {
        if (result.responseData) {
          if (result.responseData['item1'] && result.responseData['item1'].length > 0) {
            this.driveItems = result.responseData['item1'];
            this.driveItems.forEach(element => {
              element.isSelected = false;
              element.isShow = true;
              element.isDisabled = this.showFolderOnly && element.extension !== 'folder';
            });
            this.refreshItemsInItemsHistory(this.driveItems);
          } else {
            this.isFolderEmpty = true;
          }
          if (result.responseData['item2']) {
            this.nextBatchLink = result.responseData['item2'];
          } else {
            this.nextBatchLink = '';
          }
        }
      }
    });
    // if (item.id) {
    //   // this._oneDriveService.getDriveItemsByItemId(item.parentReference.driveId, item.id).subscribe(result => {
    //   //   this.isSharePointSitesLoading = false;
    //   //   if (!result.isError) {
    //   //     if (result.responseData.length > 0) {
    //   //       this.driveItems = result.responseData;
    //   //       this.driveItems.forEach(element => {
    //   //         element.isSelected = false;
    //   //         if (this.showFolderOnly) {
    //   //           if (element.extension === 'folder') {
    //   //             element.isShow = true;
    //   //           } else {
    //   //             element.isShow = false;
    //   //           }
    //   //         } else {
    //   //           element.isShow = true;
    //   //         }
    //   //       });
    //   //     } else {
    //   //       this.isFolderEmpty = true;
    //   //     }
    //   //   }
    //   // });

    //   this.isSharePointSitesLoading = true;
    //   this.nextBatchLink = '';
    //   this._oneDriveService.getMsFiles(item.id ? item.parentReference.driveId : undefined, item.id ? item.id : undefined, undefined).subscribe(result => {
    //     this.isSharePointSitesLoading = false;
    //     if (!result.isError) {
    //       if (result.responseData) {
    //         if (result.responseData['item1'] && result.responseData['item1'].length > 0) {
    //           this.driveItems = result.responseData['item1'];
    //           this.driveItems.forEach(element => {
    //             element.isSelected = false;
    //             if (this.showFolderOnly) {
    //               if (element.extension === 'folder') {
    //                 element.isShow = true;
    //               } else {
    //                 element.isShow = false;
    //               }
    //             } else {
    //               element.isShow = true;
    //             }
    //           });
    //         } else {
    //           this.isFolderEmpty = true;
    //         }
    //         if (result.responseData['item2']) {
    //           this.nextBatchLink = result.responseData['item2'];
    //         } else {
    //           this.nextBatchLink = '';
    //         }
    //       }
    //     }
    //   });
    // } else {
    //   this.isSharePointSitesLoading = true;
    //   this.nextBatchLink = '';
    //   this._oneDriveService.getMsFiles(undefined, undefined).subscribe(result => {
    //     this.isSharePointSitesLoading = false;
    //     if (!result.isError) {
    //       if (result.responseData) {
    //         if (result.responseData['item1'] && result.responseData['item1'].length > 0) {
    //           this.driveItems = result.responseData['item1'];
    //           this.driveItems.forEach(element => {
    //             element.isSelected = false;
    //             if (this.showFolderOnly) {
    //               if (element.extension === 'folder') {
    //                 element.isShow = true;
    //               } else {
    //                 element.isShow = false;
    //               }
    //             } else {
    //               element.isShow = true;
    //             }
    //           });
    //         } else {
    //           this.isFolderEmpty = true;
    //         }
    //         if (result.responseData['item2']) {
    //           this.nextBatchLink = result.responseData['item2'];
    //         } else {
    //           this.nextBatchLink = '';
    //         }
    //       }
    //     }
    //   });
    // }
  }

  refreshItems(item) {
    this.isFolderEmpty = false;
    this.isSharePointSitesLoading = true;
    this.nextBatchLink = '';
    this._oneDriveService.getMsFiles(item.id ? item.parentReference.driveId : undefined, item.id && item.parentReference?.driveId !== item.id ? item.id : undefined, undefined, undefined).subscribe(result => {
      this.isSharePointSitesLoading = false;
      if (!result.isError) {
        if (result.responseData) {
          if (result.responseData['item1'] && result.responseData['item1'].length > 0) {
            this.driveItems = result.responseData['item1'];
            this.driveItems.forEach(element => {
              element.isSelected = false;
              element.isShow = true;
              element.isDisabled = this.showFolderOnly && element.extension !== 'folder';
            });
            this.refreshItemsInItemsHistory(this.driveItems);
          } else {
            this.isFolderEmpty = true;
          }
          if (result.responseData['item2']) {
            this.nextBatchLink = result.responseData['item2'];
          } else {
            this.nextBatchLink = '';
          }
        }
      }
    });
    // if (item.id) {
    //   // this._oneDriveService.getDriveItemsByItemId(item.parentReference.driveId, item.id).subscribe(result => {
    //   //   this.isSharePointSitesLoading = false;
    //   //   if (!result.isError) {
    //   //     if (result.responseData.length > 0) {
    //   //       this.driveItems = result.responseData;
    //   //       this.driveItems.forEach(element => {
    //   //         element.isSelected = false;
    //   //         if (this.showFolderOnly) {
    //   //           if (element.extension === 'folder') {
    //   //             element.isShow = true;
    //   //           } else {
    //   //             element.isShow = false;
    //   //           }
    //   //         } else {
    //   //           element.isShow = true;
    //   //         }
    //   //       });
    //   //     } else {
    //   //       this.isFolderEmpty = true;
    //   //     }
    //   //   }
    //   // });

    //   this.isSharePointSitesLoading = true;
    //   this.nextBatchLink = '';
    //   this._oneDriveService.getMsFiles(item.id ? item.parentReference.driveId : undefined, item.id ? item.id : undefined, undefined).subscribe(result => {
    //     this.isSharePointSitesLoading = false;
    //     if (!result.isError) {
    //       if (result.responseData) {
    //         if (result.responseData['item1'] && result.responseData['item1'].length > 0) {
    //           this.driveItems = result.responseData['item1'];
    //           this.driveItems.forEach(element => {
    //             element.isSelected = false;
    //             if (this.showFolderOnly) {
    //               if (element.extension === 'folder') {
    //                 element.isShow = true;
    //               } else {
    //                 element.isShow = false;
    //               }
    //             } else {
    //               element.isShow = true;
    //             }
    //           });
    //         } else {
    //           this.isFolderEmpty = true;
    //         }
    //         if (result.responseData['item2']) {
    //           this.nextBatchLink = result.responseData['item2'];
    //         } else {
    //           this.nextBatchLink = '';
    //         }
    //       }
    //     }
    //   });
    // } else {
    //   this.isSharePointSitesLoading = true;
    //   this.nextBatchLink = '';
    //   this._oneDriveService.getMsFiles(undefined, undefined).subscribe(result => {
    //     this.isSharePointSitesLoading = false;
    //     if (!result.isError) {
    //       if (result.responseData) {
    //         if (result.responseData['item1'] && result.responseData['item1'].length > 0) {
    //           this.driveItems = result.responseData['item1'];
    //           this.driveItems.forEach(element => {
    //             element.isSelected = false;
    //             if (this.showFolderOnly) {
    //               if (element.extension === 'folder') {
    //                 element.isShow = true;
    //               } else {
    //                 element.isShow = false;
    //               }
    //             } else {
    //               element.isShow = true;
    //             }
    //           });
    //         } else {
    //           this.isFolderEmpty = true;
    //         }
    //         if (result.responseData['item2']) {
    //           this.nextBatchLink = result.responseData['item2'];
    //         } else {
    //           this.nextBatchLink = '';
    //         }
    //       }
    //     }
    //   });
    // }
  }

  triggerUploadFiles(): void {
    this.isShowCreateOptions = false;
    if (this.level === 0) {
      this.triggerFileControlL0();
    } else if (this.level === 3) {
      this.triggerFileControlL3();
    } else if (this.level === 4) {
      this.triggerFileControlL4();
    } else {
      this.triggerFileControlL5();
    }
  }

  triggerFileControlL3(): void {
    $('#fileUploadOneDrive_L3').trigger('click');
  }

  triggerFileControlL4(): void {
    $('#fileUploadOneDrive_L4').trigger('click');
  }

  triggerFileControlL5(): void {
    $('#fileUploadOneDrive_L5').trigger('click');
  }

  triggerFileControlL0(): void {
    $('#fileUploadOneDrive_L0').trigger('click');
  }

  fileInputChanged(event, isOneDrive, level): void {
    if (event.target.value) {
      if (event.target.files) {
        // Reset between invocations so a previously-rejected file doesn't
        // poison subsequent valid uploads.
        this.fileUploadError = false;
        this.fileErrorMsg = '';
        this.uploadingItems = [];
        let files = [];
        files = event.target.files;
        if (event.target.files.length > 0) {
          for (let index = 0; index < event.target.files.length; index++) {
            const element = event.target.files[index];
            if (element.size >= AppConsts.uploadFileSize) {
              this.toastr.error(`"${element.name}" is too large to upload. Maximum size is 25 MB.`, null, this.toastrOptions);
              this.fileUploadError = true;
              this.fileErrorMsg = 'Cannot upload file above 25mb.';
            } else if (element.size === 0) {
              this.toastr.error(`"${element.name}" is empty and can't be uploaded.`, null, this.toastrOptions);
              this.fileUploadError = true;
              this.fileErrorMsg = 'Cannot upload file with 0kb.';
            }
          }
          this.fileRawData = [];
          if (!this.fileUploadError) {
            let fileCts = event.target.files.length;
            for (let index = 0; index < event.target.files.length; index++) {
              const element = event.target.files[index];
              this.fileUploadError = false;
              this.fileCtrl = event.target.files[0].name;
              let file = element;
              this.fileRawData.push(file);
              fileCts--;
              if (fileCts <= 0) {
                this.isUploadFiles = true;
                if (level === 0) {
                  this.uploadFileForLevel0();
                } else {
                  this.uploadFileToSpecificFolder(isOneDrive, level);
                }
                event.target.value = null;
              }
            }
          } else {
            this.fileUploadError = false;
          }
        }
      }
    }
  }

  uploadFileForLevel0() {
    if (this.fileRawData.length > 0) {
      let fileCts = this.fileRawData.length;
      this.uploadingItems = [];
      let options = this.toastrOptions;
      this.toastr
        .success('We are uploading file(s). Please wait for a while.',
          null, options);
      this.fileRawData.forEach((element, index) => {
        let itemId = this.folderDetails.itemId;
        let driveId = this.folderDetails.driveId;
        // if (isOneDrive) {
        //   itemId = this.selectedItem.length > 1 ? this.selectedItem[this.selectedItem.length - 1].id : '';
        //   driveId = this.selectedItem.length > 1 ? this.selectedItem[this.selectedItem.length - 1].parentReference.driveId : '';
        // } else {
        //   if (level === 3) {
        //     // SharePoint Drive level
        //     driveId = this.selectedDriveData ? this.selectedDriveData.id : '';
        //   } else {
        //     // SharePoint Items level
        //     itemId = this.selectedItem.length > 0 ? this.selectedItem[this.selectedItem.length - 1].id : '';
        //     driveId = this.selectedItem.length > 0 ? this.selectedItem[this.selectedItem.length - 1].parentReference.driveId : '';
        //   }
        // }
        if (driveId === itemId) {
          itemId = undefined;
        }
        this.isSaving = true;
        this._oneDriveService.uploadFile({ data: element, fileName: element.name } as FileParameter, driveId, itemId, 0).subscribe(result => {
          if (!result.isError) {
            fileCts--;
            if (fileCts <= 0) {
              this.isSaving = false;
              let options = this.toastrOptions;
              this.toastr
                .success('File(s) uploaded successfully.',
                  null, options);
              this.refreshItemsOnL0(this.folderDetails);
              // if (level !== 3) {
              //   this.refreshItems(this.selectedItem[this.selectedItem.length - 1]);
              // } else {
              //   this.goToChildrenOfDrive(this.selectedDriveData);
              // }
            }
          } else {
            this.isSaving = false;
            if (result.errorMessage === '103') {
              this.information = 'You do not have the necessary permissions to upload or create files/folders in this location. Please choose a different destination or contact your administrator for access.';
              this.isShowInfoPopup = true;
            } else {
              this.fileUploadError = true;
              this.fileErrorMsg = 'Something went wrong while uploading file.';
            }
          }
        });
      });
    }
  }

  uploadFileToSpecificFolder(isOneDrive, level) {
    if (this.fileRawData.length > 0) {
      let fileCts = this.fileRawData.length;
      this.uploadingItems = [];
      let options = this.toastrOptions;
      this.toastr
        .success('We are uploading file(s). Please wait for a while.',
          null, options);
      this.fileRawData.forEach((element, index) => {
        let itemId = '';
        let driveId = '';
        if (isOneDrive) {
          itemId = this.selectedItem.length > 1 ? this.selectedItem[this.selectedItem.length - 1].id : '';
          driveId = this.selectedItem.length > 1 ? this.selectedItem[this.selectedItem.length - 1].parentReference.driveId : '';
        } else {
          if (level === 3) {
            // SharePoint Drive level
            driveId = this.selectedDriveData ? this.selectedDriveData.id : '';
          } else {
            // SharePoint Items level
            itemId = this.selectedItem.length > 0 ? this.selectedItem[this.selectedItem.length - 1].id : '';
            driveId = this.selectedItem.length > 0 ? this.selectedItem[this.selectedItem.length - 1].parentReference.driveId : '';
          }
        }
        if (driveId === itemId) {
          itemId = undefined;
        }
        this.isSaving = true;
        this._oneDriveService.uploadFile({ data: element, fileName: element.name } as FileParameter, driveId, itemId, 0).subscribe(result => {
          if (!result.isError) {
            fileCts--;
            if (fileCts <= 0) {
              this.isSaving = false;
              let options = this.toastrOptions;
              this.toastr
                .success('File(s) uploaded successfully.',
                  null, options);
              if (level !== 3) {
                this.refreshItems(this.selectedItem[this.selectedItem.length - 1]);
              } else {
                this.goToChildrenOfDrive(this.selectedDriveData);
              }
            }
          } else {
            this.isSaving = false;
            if (result.errorMessage === '103') {
              this.information = 'You do not have the necessary permissions to upload or create files/folders in this location. Please choose a different destination or contact your administrator for access.';
              this.isShowInfoPopup = true;
            } else {
              this.fileUploadError = true;
              this.fileErrorMsg = 'Something went wrong while uploading file.';
            }
          }
        });
      });
    }
  }
}
